/**
 * Right detail drawer of the batch workspace — replaces the item mega-dialog
 * (UX plan §4.4, D8 fix). Persistent on md+ screens, temporary below.
 *
 * Content order (progressive disclosure L3):
 *   preview → validation chips → editable copy fields → 3 actions
 *   (Regenerate image · Variate copy · Make reel video) → accordions
 *   «Prompt & style» and «Generation history».
 *
 * The shipped AI-video panel renders as the drawer's video mode for
 * video-first items — internals unchanged (§4.10 mapping).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImageIcon from '@mui/icons-material/Image';
import NotesIcon from '@mui/icons-material/Notes';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import AddIcon from '@mui/icons-material/Add';
import ArchiveIcon from '@mui/icons-material/Archive';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RestoreIcon from '@mui/icons-material/Restore';
import { StyledTextField } from '@modules/ui-components';
import type {
  AdCreativeAssetReference,
  AdCreativeDestinationRef,
  AdCreativeItem,
  AdCreativeLocalizationManifest,
  AdCreativeLocalizationOperation,
  AdCreativeLocalizedMediaEntry,
} from '@segmently/schema-models/ad-creative';
import type { AiAssetExperiment } from '@segmently/schema-models/content-plan';
import {
  useLocaleAuthoring,
  useProjectLocales,
  type ProjectLocaleGlossary,
  type ProjectLocaleProfile,
} from '@modules/locale-config';
import { useTaskTransition } from '@modules/background-tasks';
import { countUnicodeGraphemes, localeDirection } from '@modules/shared-locales';
import { AD_CREATIVE_BATCH, AD_CREATIVE_INSIGHT } from '../../selectors';
import type {
  AdCreativeApiClient,
  AdCreativeCaptionReviewView,
  AdCreativeAiTextLocalizationContext,
  AdCreativeImageLocalizationMode,
  AdCreativeLocalizationComponent,
  AdCreativeLocalizationJobResponse,
  AdCreativeLocalizationProjection,
  AdCreativeLocalizationPreflight,
  AdCreativeLocalizationPreflightRequest,
  AdCreativeLocalizationStartRequest,
  AdCreativeSourceImageEvidenceView,
  AdCreativeDestinationCatalogView,
  AdCreativeDestinationOptionView,
  AdCreativeMediaLocalizationJobView,
  AdCreativeMediaLocalizationTaskView,
  AdCreativeTextLocalizationJobView,
  AdCreativeTextLocalizationTaskView,
  AdCreativeVideoLocalizationPreflightRequest,
  AdCreativeVideoReviewComponentBindingView,
  AdCreativeVideoReviewSlotBindingView,
  AdCreativeStagedVideoUploadProgress,
} from '../../services/AdCreativeApiClient';
import {
  INSIGHT_CATEGORY_COLOR,
  type AdCreativeInsightIndex,
} from '../../services/AdCreativeInsightContextClient';
import type { AdCreativeAngle } from './batchModel';
import { isVideoCreativeItem } from '../AdCreativeVideoSection';
import { VideoStudio } from '../video/VideoStudio';
import { VideoTranscriptPanel } from '../localization/VideoTranscriptPanel';
import {
  VideoTextInspectionPanel,
  type AdCreativeVideoTextInspectionSelection,
} from '../localization/VideoTextInspectionPanel';
import {
  CulturalVisualReviewPanel,
  type AdCreativeCulturalVisualSelection,
} from '../localization/CulturalVisualReviewPanel';
import { CarouselLocalizationEditor } from '../localization/CarouselLocalizationEditor';
import {
  ImageLocalizationReviewPanel,
  type AdCreativeImageReviewGate,
} from '../localization/ImageLocalizationReviewPanel';
import {
  VideoVoiceRightsPanel,
  type AdCreativeVideoVoiceRightsSelection,
} from '../localization/VideoVoiceRightsPanel';
import { ApprovedAudioCandidatePanel } from '../localization/ApprovedAudioCandidatePanel';
import { getItemPreviewImageUrl } from './batchModel';
import { MediaLightbox } from '@modules/ui-components/dialogs/MediaLightbox';
import {
  CLAIM_FIELDS,
  CreativeMetaSummary,
  claimStateForField,
  type CopyFieldKey,
} from './CreativeMetaSummary';
import {
  CREATIVE_LOCALIZED_COPY_FIELDS,
  creativeLocaleHeadline,
  findCreativeLocaleKey,
  getCreativeLocaleStatus,
  getExactCreativeLocaleCopy,
  isSegmentlyOwnedCreativeItem,
  resolveCreativeLocaleProjection,
  type CreativeLocalizedCopyField,
} from '../../services/creativeLocaleProjection';

export const DRAWER_WIDTH = 420;
const LOCALIZATION_SAFETY_REFRESH_INTERVAL_MS = 30_000;
const DEFAULT_IMAGE_LOCALIZATION_MODEL = 'gemini-3.1-flash-image';
const DEFAULT_VIDEO_LOCALIZATION_MODEL = 'gemini-3-flash-preview';
const DEFAULT_CULTURAL_VIDEO_LOCALIZATION_MODEL = 'veo-3.1-fast-generate-preview';
const VIDEO_TRANSCRIPTION_MODEL = 'gemini-3-flash-preview';
const MANUAL_LOCALIZED_VIDEO_MAX_BYTES = 30 * 1024 * 1024;
const MANUAL_LOCALIZED_VIDEO_STAGED_MAX_BYTES = 128 * 1024 * 1024;
type DrawerDestinationMode = 'funnel' | 'article' | 'url' | 'none';
type CreativeCopyConstraint = { maxChars?: number; countingUnit: 'grapheme_cluster' };
type ManualMediaUploadEvidenceView = {
  evidenceId: string;
  byteSize: number;
  objectGeneration?: string;
  mediaKind: 'image' | 'poster' | 'video';
  asset: AdCreativeAssetReference;
};

interface DrawerLocalizationJobSnapshot {
  component: AdCreativeLocalizationComponent;
  job: AdCreativeTextLocalizationJobView | AdCreativeMediaLocalizationJobView;
  task?: AdCreativeTextLocalizationTaskView | AdCreativeMediaLocalizationTaskView;
}

interface AdCreativeVideoReviewGate {
  canAccept: boolean;
  reason: string;
}

const COPY_FIELDS: Array<{ key: CreativeLocalizedCopyField; label: string; multiline?: boolean }> = [
  { key: 'headline', label: 'Headline' },
  { key: 'primaryText', label: 'Primary text', multiline: true },
  { key: 'description', label: 'Description' },
  { key: 'cta', label: 'CTA' },
  { key: 'overlayText', label: 'Overlay text' },
  { key: 'subtitleScript', label: 'Subtitle / spoken script', multiline: true },
];

interface CreativeDetailDrawerProps {
  projectId: string;
  client: AdCreativeApiClient;
  item: AdCreativeItem | null;
  onClose: () => void;
  onItemUpdated: (item: AdCreativeItem) => void;
  onRefreshItem: (itemId: string) => Promise<void> | void;
  onRegenerateImage: (item: AdCreativeItem) => Promise<void> | void;
  onVariateCopy: (item: AdCreativeItem) => Promise<void> | void;
  /** FR2: image items offer «Make video from this creative» (video variant). */
  onMakeVideo?: (item: AdCreativeItem) => Promise<void> | void;
  /** FR5: open the fullscreen storyboard workspace. */
  onOpenStoryboard?: (itemId: string) => void;
  /** The item's angle (Video Studio grounded banner + scene hints). */
  angle?: AdCreativeAngle | null;
  /** insightId -> thesis payload of the pinned analysis. */
  insightIndex?: AdCreativeInsightIndex;
  /** Exact locale selected by a read-only report handoff. */
  initialLocale?: string;
}

export const CreativeDetailDrawer = React.memo(function CreativeDetailDrawer({
  projectId,
  client,
  item,
  onClose,
  onItemUpdated,
  onRefreshItem,
  onRegenerateImage,
  onVariateCopy,
  onMakeVideo,
  onOpenStoryboard,
  angle,
  insightIndex,
  initialLocale,
}: CreativeDetailDrawerProps): React.ReactElement {
  const theme = useTheme();
  const isPersistent = useMediaQuery(theme.breakpoints.up('md'));
  const {
    locales: projectLocales,
    loading: localesLoading,
    configured: projectLocaleConfigStored,
  } = useProjectLocales();
  const editableItem = item && isSegmentlyOwnedCreativeItem(item) ? item : null;
  const localeAuthoring = useLocaleAuthoring(editableItem ? projectId : undefined);
  const [selectedLocale, setSelectedLocale] = useState('');
  const [copyDraft, setCopyDraft] = useState<Partial<Record<CreativeLocalizedCopyField, string>>>({});
  const [copyFieldConstraints, setCopyFieldConstraints] = useState<Partial<Record<CreativeLocalizedCopyField, CreativeCopyConstraint>>>({});
  const [touchedFields, setTouchedFields] = useState<Set<CreativeLocalizedCopyField>>(() => new Set());
  const [showFallback, setShowFallback] = useState(true);
  const [compareWithSource, setCompareWithSource] = useState(false);
  const [showAddLocale, setShowAddLocale] = useState(false);
  const [newLocale, setNewLocale] = useState('');
  const [defaultLocaleConfirmationOpen, setDefaultLocaleConfirmationOpen] = useState(false);
  const [localeMutating, setLocaleMutating] = useState(false);
  const [localizationIndex, setLocalizationIndex] = useState<AdCreativeLocalizationProjection | null>(null);
  const [localizationIndexLoading, setLocalizationIndexLoading] = useState(false);
  const [localizationIndexError, setLocalizationIndexError] = useState<string | null>(null);
  const [componentReviewNotes, setComponentReviewNotes] = useState('');
  const [mediaMutatingSlot, setMediaMutatingSlot] = useState<string | null>(null);
  const [manualVideoUploadProgress, setManualVideoUploadProgress] = useState<(
    AdCreativeStagedVideoUploadProgress & { slotKey: string }
  ) | null>(null);
  const [manualMediaEvidence, setManualMediaEvidence] = useState<Record<string, ManualMediaUploadEvidenceView>>({});
  const [destinationMode, setDestinationMode] = useState<DrawerDestinationMode>('url');
  const [destinationUrl, setDestinationUrl] = useState('');
  const [destinationReference, setDestinationReference] = useState('');
  const [destinationTouched, setDestinationTouched] = useState(false);
  const [destinationSaving, setDestinationSaving] = useState(false);
  const [destinationCatalog, setDestinationCatalog] = useState<AdCreativeDestinationCatalogView | null>(null);
  const [destinationCatalogLoading, setDestinationCatalogLoading] = useState(false);
  const [destinationCatalogError, setDestinationCatalogError] = useState<string | null>(null);
  const [destinationFallbackApproved, setDestinationFallbackApproved] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [actionRunning, setActionRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localizationComponent, setLocalizationComponent] = useState<AdCreativeLocalizationComponent>('text');
  const [localizationTargetMarket, setLocalizationTargetMarket] = useState('');
  const [localizationTone, setLocalizationTone] = useState('');
  const [localizationMarketDirection, setLocalizationMarketDirection] = useState('');
  const [localizationGlossary, setLocalizationGlossary] = useState('');
  const [selectedLocaleProfileId, setSelectedLocaleProfileId] = useState('');
  const [selectedLocaleGlossaryId, setSelectedLocaleGlossaryId] = useState('');
  const [localizationClaims, setLocalizationClaims] = useState('');
  const [localizationProtectedTokens, setLocalizationProtectedTokens] = useState('');
  const [localizationInstruction, setLocalizationInstruction] = useState('');
  const [imageLocalizationMode, setImageLocalizationMode] = useState<AdCreativeImageLocalizationMode>('reuse_when_language_neutral');
  const [imageSourceEvidence, setImageSourceEvidence] = useState<Record<string, AdCreativeSourceImageEvidenceView>>({});
  const [imageTargetVisibleText, setImageTargetVisibleText] = useState<Record<string, string>>({});
  const [imageInspectionBusy, setImageInspectionBusy] = useState(false);
  const [videoSlotKey, setVideoSlotKey] = useState('');
  const [videoVoiceEnabled, setVideoVoiceEnabled] = useState(false);
  const [videoVoiceMode, setVideoVoiceMode] = useState<'replace_with_tts' | 'dub_when_supported'>('replace_with_tts');
  const [videoVoiceRightsSelection, setVideoVoiceRightsSelection] = useState<AdCreativeVideoVoiceRightsSelection | null>(null);
  const [selectedApprovedAudioArtifactId, setSelectedApprovedAudioArtifactId] = useState('');
  const [videoOnScreenTextEnabled, setVideoOnScreenTextEnabled] = useState(false);
  const [videoTextInspectionSelection, setVideoTextInspectionSelection] = useState<AdCreativeVideoTextInspectionSelection | null>(null);
  const [videoCulturalVisualEnabled, setVideoCulturalVisualEnabled] = useState(false);
  const [culturalVisualSelection, setCulturalVisualSelection] = useState<AdCreativeCulturalVisualSelection | null>(null);
  const [localizationPreflight, setLocalizationPreflight] = useState<AdCreativeLocalizationPreflight | null>(null);
  const [localizationJob, setLocalizationJob] = useState<DrawerLocalizationJobSnapshot | null>(null);
  const [localizationConfirmed, setLocalizationConfirmed] = useState(false);
  const [localizationBusy, setLocalizationBusy] = useState(false);
  const [localizationActionBusy, setLocalizationActionBusy] = useState(false);
  const [localizationError, setLocalizationError] = useState<string | null>(null);
  const [captionReview, setCaptionReview] = useState<AdCreativeCaptionReviewView | null>(null);
  const [captionReviewLoading, setCaptionReviewLoading] = useState(false);
  const [captionReviewError, setCaptionReviewError] = useState<string | null>(null);
  const [imageReviewGate, setImageReviewGate] = useState<AdCreativeImageReviewGate>({
    state: 'empty',
    canAccept: false,
    reason: 'No current AI image evidence has been loaded.',
  });
  const [videoReviewGate, setVideoReviewGate] = useState<AdCreativeVideoReviewGate>({
    canAccept: false,
    reason: 'The exact localized derivative has not loaded yet.',
  });
  const localizationRunKeyRef = useRef('');
  const captionReviewRequestRef = useRef('');
  const imageInspectionScopeRef = useRef('');
  const localeResourceSelectionScopeRef = useRef('');
  const initialLocaleAppliedRef = useRef('');
  const localizationManifestCacheRef = useRef<{ key: string; manifest: AdCreativeLocalizationManifest } | null>(null);
  const localizationManifestRequestRef = useRef<{
    key: string;
    promise: Promise<AdCreativeLocalizationManifest>;
  } | null>(null);
  // FR1: preview click opens the lightbox ABOVE the drawer (single overlay
  // level — the drawer stays mounted beneath, AC-003).
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => () => {
    localizationRunKeyRef.current = 'unmounted';
    imageInspectionScopeRef.current = 'unmounted';
    captionReviewRequestRef.current = 'unmounted';
  }, []);

  useEffect(() => {
    if (!editableItem) {
      setSelectedLocale('');
      initialLocaleAppliedRef.current = '';
      return;
    }
    const initialLocaleKey = `${editableItem.id}:${initialLocale ?? ''}`;
    const pinnedLocale = initialLocaleAppliedRef.current !== initialLocaleKey && initialLocale
      ? findCreativeLocaleKey(editableItem, initialLocale)
      : null;
    initialLocaleAppliedRef.current = initialLocaleKey;
    setSelectedLocale((current) => pinnedLocale
      ?? findCreativeLocaleKey(editableItem, current)
      ?? editableItem.defaultLanguage);
  }, [editableItem?.id, editableItem?.localeRevision, initialLocale]);

  useEffect(() => {
    if (!editableItem || !selectedLocale) {
      setCopyDraft({});
      setTouchedFields(new Set());
      return;
    }
    const exact = getExactCreativeLocaleCopy(editableItem, selectedLocale);
    setCopyDraft(Object.fromEntries(
      COPY_FIELDS.map(({ key }) => [key, exact[key] ?? '']),
    ));
    const exactDestination = getExactDestination(editableItem, selectedLocale);
    setDestinationMode(destinationModeFromRef(exactDestination));
    setDestinationUrl(exactDestination?.type === 'url' ? exactDestination.url ?? '' : '');
    setDestinationReference(exactDestination?.type === 'web_placement'
      ? exactDestination.webPlacementAlias ?? ''
      : exactDestination?.type === 'article'
        ? exactDestination.articleId ?? ''
        : '');
    setDestinationTouched(false);
    setDestinationFallbackApproved(false);
    setTouchedFields(new Set());
    setError(null);
    setSaveStatus(null);
    setDefaultLocaleConfirmationOpen(false);
  }, [editableItem?.id, editableItem?.localeRevision, selectedLocale]);

  useEffect(() => {
    if (!editableItem) {
      setLocalizationIndex(null);
      setLocalizationIndexError(null);
      setLocalizationIndexLoading(false);
      return undefined;
    }
    let active = true;
    setLocalizationIndexLoading(true);
    setLocalizationIndexError(null);
    void client.listItemLocalizations(projectId, editableItem.id).then((response) => {
      if (!active) return;
      if (!response.success || !response.data?.localizations) {
        throw new Error(response.error || 'Failed to load localization review state.');
      }
      setLocalizationIndex(response.data.localizations);
    }).catch((nextError) => {
      if (!active) return;
      setLocalizationIndex(null);
      setLocalizationIndexError(nextError instanceof Error ? nextError.message : String(nextError));
    }).finally(() => {
      if (active) setLocalizationIndexLoading(false);
    });
    return () => { active = false; };
  }, [client, projectId, editableItem?.id, editableItem?.localeRevision, editableItem?.sourceRevision]);

  useEffect(() => {
    localizationManifestCacheRef.current = null;
    localizationManifestRequestRef.current = null;
    setCopyFieldConstraints({});
    setManualMediaEvidence({});
  }, [editableItem?.id, editableItem?.sourceRevision]);

  const projection = useMemo(
    () => editableItem && selectedLocale
      ? resolveCreativeLocaleProjection(editableItem, selectedLocale, showFallback ? 'preview' : 'strict')
      : null,
    [editableItem, selectedLocale, showFallback],
  );
  const sourceProjection = useMemo(
    () => editableItem
      ? resolveCreativeLocaleProjection(editableItem, editableItem.defaultLanguage, 'strict')
      : null,
    [editableItem],
  );
  const selectedLocalizationVariant = useMemo(
    () => localizationIndex?.variants.find((variant) => variant.targetLocale === selectedLocale) ?? null,
    [localizationIndex, selectedLocale],
  );
  const reviewableComponents = useMemo(() => {
    if (!selectedLocalizationVariant) return [];
    return (['text', 'images', 'video', 'destination'] as const)
      .filter((component) => selectedLocalizationVariant.componentStatus[component] !== 'missing'
        && selectedLocalizationVariant.componentStatus[component] !== 'archived');
  }, [selectedLocalizationVariant]);
  const localeComponentsAccepted = reviewableComponents.length > 0
    && reviewableComponents.every((component) => (
      selectedLocalizationVariant?.componentStatus[component] === 'ready'
    ));
  const sourceMediaSlots = useMemo(() => {
    if (!editableItem || !sourceProjection) return [];
    return Object.entries(editableItem.localizedMedia ?? {}).filter(([, slot]) => (
      Object.prototype.hasOwnProperty.call(slot.translations ?? {}, sourceProjection.sourceLocale)
    ));
  }, [editableItem, sourceProjection]);
  const sourceImageSlotKeys = useMemo(() => sourceMediaSlots
    .filter(([, slot]) => slot.mediaKind === 'image' || slot.mediaKind === 'poster')
    .map(([slotKey]) => slotKey), [sourceMediaSlots]);
  const generatedTargetImageSlotKeys = useMemo(() => sourceMediaSlots
    .filter(([, slot]) => slot.mediaKind === 'image' || slot.mediaKind === 'poster')
    .filter(([, slot]) => getExactMediaEntry(slot.translations ?? {}, selectedLocale)?.provenance === 'generated')
    .map(([slotKey]) => slotKey)
    .sort(), [selectedLocale, sourceMediaSlots]);
  const requiresAiImageReview = Boolean(
    selectedLocalizationVariant
    && !projection?.isSource
    && generatedTargetImageSlotKeys.length > 0,
  );
  const imageReviewBinding = selectedLocalizationVariant?.componentBindings?.images;
  const imageReviewBindingScope = useMemo(() => JSON.stringify({
    bindingVersion: imageReviewBinding?.bindingVersion ?? 0,
    sourceComponentHash: imageReviewBinding?.sourceComponentHash ?? '',
    currentSourceComponentHash: selectedLocalizationVariant?.sourceComponentHashes?.images ?? '',
    componentStatus: selectedLocalizationVariant?.componentStatus.images ?? '',
    targetLocale: imageReviewBinding?.targetLocale ?? '',
    slots: Object.entries(imageReviewBinding?.slots ?? {}).sort(([left], [right]) => left.localeCompare(right)),
  }), [imageReviewBinding, selectedLocalizationVariant]);
  const sourceImageInspectionScope = useMemo(() => JSON.stringify({
    itemId: editableItem?.id ?? '',
    sourceRevision: editableItem?.sourceRevision ?? '',
    slotKeys: [...sourceImageSlotKeys].sort(),
  }), [editableItem?.id, editableItem?.sourceRevision, sourceImageSlotKeys]);
  imageInspectionScopeRef.current = sourceImageInspectionScope;
  const imageTargetTextScope = useMemo(() => JSON.stringify({
    sourceImageInspectionScope,
    selectedLocale,
  }), [selectedLocale, sourceImageInspectionScope]);
  const sourceVideoSlotKeys = useMemo(() => sourceMediaSlots
    .filter(([, slot]) => slot.mediaKind === 'video')
    .map(([slotKey]) => slotKey), [sourceMediaSlots]);
  const generatedTargetVideoSlotKeys = useMemo(() => sourceMediaSlots
    .filter(([, slot]) => slot.mediaKind === 'video')
    .filter(([, slot]) => getExactMediaEntry(slot.translations ?? {}, selectedLocale)?.provenance === 'generated')
    .map(([slotKey]) => slotKey)
    .sort(), [selectedLocale, sourceMediaSlots]);
  const requiresAiVideoReview = Boolean(
    selectedLocalizationVariant
    && !projection?.isSource
    && generatedTargetVideoSlotKeys.length > 0,
  );
  const videoReviewBinding = selectedLocalizationVariant?.componentBindings?.video;
  const videoReviewSlotBinding = videoSlotKey
    ? videoReviewBinding?.slots?.[videoSlotKey]
    : undefined;
  const exactTargetCaptionArtifactId = videoReviewBinding?.targetLocale === selectedLocale
    && videoReviewBinding.sourceComponentHash === selectedLocalizationVariant?.sourceComponentHashes.video
    && selectedLocalizationVariant?.componentStatus.video !== 'stale'
    && selectedLocalizationVariant?.componentStatus.video !== 'missing'
    && selectedLocalizationVariant?.componentStatus.video !== 'archived'
    ? videoReviewSlotBinding?.captionArtifactId
    : undefined;
  const videoReviewBindingScope = useMemo(() => JSON.stringify({
    bindingVersion: videoReviewBinding?.bindingVersion ?? 0,
    targetLocale: videoReviewBinding?.targetLocale ?? '',
    sourceComponentHash: videoReviewBinding?.sourceComponentHash ?? '',
    currentSourceComponentHash: selectedLocalizationVariant?.sourceComponentHashes?.video ?? '',
    componentStatus: selectedLocalizationVariant?.componentStatus.video ?? '',
    generatedTargetVideoSlotKeys,
    selectedSlotKey: videoSlotKey,
    selectedSlot: videoReviewSlotBinding ?? null,
  }), [
    generatedTargetVideoSlotKeys,
    selectedLocalizationVariant?.componentStatus.video,
    selectedLocalizationVariant?.sourceComponentHashes?.video,
    videoReviewBinding,
    videoReviewSlotBinding,
    videoSlotKey,
  ]);
  const captionPlaybackSource = useMemo(() => resolveCaptionPlaybackSource({
    item: editableItem,
    review: captionReview,
    binding: videoReviewSlotBinding,
    componentBinding: videoReviewBinding,
    currentSourceComponentHash: selectedLocalizationVariant?.sourceComponentHashes?.video,
    currentComponentStatus: selectedLocalizationVariant?.componentStatus.video,
    selectedLocale,
    videoSlotKey,
  }), [
    captionReview,
    editableItem,
    selectedLocalizationVariant?.sourceComponentHashes?.video,
    selectedLocalizationVariant?.componentStatus.video,
    selectedLocale,
    videoReviewBinding,
    videoReviewSlotBinding,
    videoSlotKey,
  ]);

  useEffect(() => {
    captionReviewRequestRef.current = videoReviewBindingScope;
    setCaptionReview(null);
    setCaptionReviewError(null);
    setCaptionReviewLoading(false);
    setVideoReviewGate({
      canAccept: false,
      reason: 'The exact localized derivative has not loaded yet.',
    });
  }, [videoReviewBindingScope]);

  useEffect(() => {
    setSelectedApprovedAudioArtifactId('');
  }, [
    editableItem?.id,
    editableItem?.sourceRevision,
    selectedLocale,
    videoSlotKey,
    exactTargetCaptionArtifactId,
    videoVoiceRightsSelection?.evidenceId,
    videoVoiceRightsSelection?.voiceId,
  ]);

  const compatibleLocaleProfiles = useMemo(() => {
    if (!editableItem || !selectedLocale || localeAuthoring.error) return [];
    return localeAuthoring.profiles
      .filter((profile) => isExactLocaleResourceMatch(
        profile,
        projectId,
        editableItem.defaultLanguage,
        selectedLocale,
      ))
      .sort((left, right) => left.id.localeCompare(right.id));
  }, [
    editableItem,
    localeAuthoring.error,
    localeAuthoring.profiles,
    projectId,
    selectedLocale,
  ]);
  const compatibleLocaleGlossaries = useMemo(() => {
    if (!editableItem || !selectedLocale || localeAuthoring.error) return [];
    return localeAuthoring.glossaries
      .filter((glossary) => isExactLocaleResourceMatch(
        glossary,
        projectId,
        editableItem.defaultLanguage,
        selectedLocale,
      ))
      .sort((left, right) => left.id.localeCompare(right.id));
  }, [
    editableItem,
    localeAuthoring.error,
    localeAuthoring.glossaries,
    projectId,
    selectedLocale,
  ]);
  const selectedLocaleProfile = useMemo(
    () => compatibleLocaleProfiles.find((profile) => profile.id === selectedLocaleProfileId),
    [compatibleLocaleProfiles, selectedLocaleProfileId],
  );
  const selectedLocaleGlossary = useMemo(
    () => compatibleLocaleGlossaries.find((glossary) => glossary.id === selectedLocaleGlossaryId),
    [compatibleLocaleGlossaries, selectedLocaleGlossaryId],
  );
  const linkedCompatibleGlossary = useMemo(() => (
    selectedLocaleProfile?.glossaryRef
      ? compatibleLocaleGlossaries.find((glossary) => glossary.id === selectedLocaleProfile.glossaryRef)
      : undefined
  ), [compatibleLocaleGlossaries, selectedLocaleProfile]);
  const localeResourceSelectionScope = useMemo(() => JSON.stringify({
    projectId,
    itemId: editableItem?.id ?? '',
    sourceLocale: editableItem?.defaultLanguage ?? '',
    targetLocale: selectedLocale,
    error: localeAuthoring.error?.message ?? '',
    profiles: compatibleLocaleProfiles.map((profile) => `${profile.id}@${profile.version}`),
    glossaries: compatibleLocaleGlossaries.map((glossary) => `${glossary.id}@${glossary.version}`),
  }), [
    compatibleLocaleGlossaries,
    compatibleLocaleProfiles,
    editableItem?.defaultLanguage,
    editableItem?.id,
    localeAuthoring.error,
    projectId,
    selectedLocale,
  ]);

  useEffect(() => {
    if (localeAuthoring.loading || localeResourceSelectionScopeRef.current === localeResourceSelectionScope) return;
    localeResourceSelectionScopeRef.current = localeResourceSelectionScope;
    const profile = compatibleLocaleProfiles.length === 1 ? compatibleLocaleProfiles[0] : undefined;
    const glossary = profile?.glossaryRef
      ? compatibleLocaleGlossaries.find((candidate) => candidate.id === profile.glossaryRef)
      : undefined;
    setSelectedLocaleProfileId(profile?.id ?? '');
    setSelectedLocaleGlossaryId(glossary?.id ?? '');
  }, [
    compatibleLocaleGlossaries,
    compatibleLocaleProfiles,
    localeAuthoring.loading,
    localeResourceSelectionScope,
  ]);

  useEffect(() => {
    setVideoSlotKey((current) => sourceVideoSlotKeys.includes(current)
      ? current
      : sourceVideoSlotKeys[0] ?? '');
  }, [editableItem?.id, sourceVideoSlotKeys]);

  useEffect(() => {
    setVideoVoiceEnabled(false);
    setVideoVoiceMode('replace_with_tts');
    setVideoVoiceRightsSelection(null);
    setVideoOnScreenTextEnabled(false);
    setVideoTextInspectionSelection(null);
    setVideoCulturalVisualEnabled(false);
    setCulturalVisualSelection(null);
  }, [editableItem?.id, editableItem?.sourceRevision, selectedLocale, videoSlotKey]);

  useEffect(() => {
    setImageSourceEvidence({});
    setImageInspectionBusy(false);
  }, [sourceImageInspectionScope]);

  useEffect(() => {
    setImageTargetVisibleText({});
  }, [imageTargetTextScope]);

  useEffect(() => {
    setImageReviewGate({
      state: requiresAiImageReview ? 'loading' : 'empty',
      canAccept: !requiresAiImageReview,
      reason: requiresAiImageReview
        ? 'Loading exact AI image verification evidence.'
        : 'No AI-generated target images require verifier evidence.',
    });
  }, [
    editableItem?.id,
    imageReviewBindingScope,
    requiresAiImageReview,
    selectedLocale,
  ]);

  useEffect(() => {
    localizationRunKeyRef.current = JSON.stringify({
      itemId: editableItem?.id ?? '',
      sourceRevision: editableItem?.sourceRevision ?? '',
      selectedLocale,
      localizationComponent,
      imageLocalizationMode,
      videoSlotKey,
      videoVoiceEnabled,
      videoVoiceMode,
      videoVoiceEvidenceId: videoVoiceRightsSelection?.evidenceId ?? '',
      videoVoiceId: videoVoiceRightsSelection?.voiceId ?? '',
      approvedAudioArtifactId: selectedApprovedAudioArtifactId,
      videoOnScreenTextEnabled,
      videoTextInspectionEvidenceId: videoTextInspectionSelection?.evidence.evidenceId ?? '',
      videoTextInspectionEvidenceHash: videoTextInspectionSelection?.evidence.contentHash ?? '',
      videoTextReplacements: videoTextInspectionSelection?.replacements ?? [],
      videoCulturalVisualEnabled,
      culturalVisualEvidenceId: culturalVisualSelection?.evidence.evidenceId ?? '',
      culturalVisualEvidenceHash: culturalVisualSelection?.evidence.contentHash ?? '',
      localizationTargetMarket,
      localizationTone,
      localizationMarketDirection,
      localizationGlossary,
      selectedLocaleProfileId,
      selectedLocaleGlossaryId,
      localeResourceSelectionScope,
      localizationClaims,
      localizationProtectedTokens,
      localizationInstruction,
    });
    setLocalizationPreflight(null);
    setLocalizationJob(null);
    setLocalizationConfirmed(false);
    setLocalizationBusy(false);
    setLocalizationActionBusy(false);
    setLocalizationError(null);
  }, [
    editableItem?.id,
    editableItem?.sourceRevision,
    selectedLocale,
    localizationComponent,
    imageLocalizationMode,
    videoSlotKey,
    videoVoiceEnabled,
    videoVoiceMode,
    videoVoiceRightsSelection?.evidenceId,
    videoVoiceRightsSelection?.voiceId,
    selectedApprovedAudioArtifactId,
    videoOnScreenTextEnabled,
    videoTextInspectionSelection,
    videoCulturalVisualEnabled,
    culturalVisualSelection,
    localizationTargetMarket,
    localizationTone,
    localizationMarketDirection,
    localizationGlossary,
    selectedLocaleProfileId,
    selectedLocaleGlossaryId,
    localeResourceSelectionScope,
    localizationClaims,
    localizationProtectedTokens,
    localizationInstruction,
  ]);
  const exactDestination = editableItem && selectedLocale
    ? getExactDestination(editableItem, selectedLocale)
    : undefined;
  const sourceDestination = editableItem && sourceProjection
    ? getExactDestination(editableItem, sourceProjection.sourceLocale)
    : undefined;
  const destinationUsesFallback = !exactDestination && Boolean(showFallback && sourceDestination);
  const previewDestination = exactDestination ?? (destinationUsesFallback ? sourceDestination : undefined);
  const destinationOptions = useMemo(
    () => destinationCatalog?.options.filter((option) => option.kind === destinationMode) ?? [],
    [destinationCatalog, destinationMode],
  );
  const selectedDestinationOption = useMemo(
    () => destinationOptions.find((option) => option.id === destinationReference),
    [destinationOptions, destinationReference],
  );

  useEffect(() => {
    if (!editableItem || !selectedLocale || typeof client.listItemDestinations !== 'function') {
      setDestinationCatalog(null);
      setDestinationCatalogError(null);
      return;
    }
    let active = true;
    setDestinationCatalogLoading(true);
    setDestinationCatalogError(null);
    void client.listItemDestinations(projectId, editableItem.id, selectedLocale, {
      fallbackApproved: destinationFallbackApproved,
    }).then((response) => {
      if (!active) return;
      if (!response.success || !response.data?.catalog) {
        throw new Error(response.error || 'Failed to load project destinations.');
      }
      setDestinationCatalog(response.data.catalog);
    }).catch((nextError) => {
      if (!active) return;
      setDestinationCatalog(null);
      setDestinationCatalogError(nextError instanceof Error ? nextError.message : String(nextError));
    }).finally(() => {
      if (active) setDestinationCatalogLoading(false);
    });
    return () => { active = false; };
  }, [
    client,
    projectId,
    editableItem?.id,
    editableItem?.localeRevision,
    selectedLocale,
    destinationFallbackApproved,
  ]);
  const previewImage = projection?.previewUrl
    ?? (projection?.isSource && editableItem ? getItemPreviewImageUrl(editableItem) : undefined);
  const localizedTitle = editableItem && selectedLocale
    ? creativeLocaleHeadline(editableItem, selectedLocale, showFallback ? 'preview' : 'strict')
    : '';
  // Stable identity (FR0/F3): VideoStudio keeps its subscriptions armed across
  // drawer re-renders instead of re-arming on every inline-arrow recreation.
  const itemId = editableItem?.id ?? null;
  const refreshCurrentItem = useCallback(() => {
    if (itemId) return onRefreshItem(itemId);
    return undefined;
  }, [itemId, onRefreshItem]);
  const copyChanged = touchedFields.size > 0;
  const copyLimitViolation = useMemo(() => Array.from(touchedFields).some((field) => {
    const limit = copyFieldConstraints[field]?.maxChars;
    return Boolean(limit && countUnicodeGraphemes(String(copyDraft[field] ?? '')) > limit);
  }), [copyDraft, copyFieldConstraints, touchedFields]);
  const localizationRunning = Boolean(localizationJob && !isLocalizationSnapshotTerminal(localizationJob));
  const contentMutating = saving
    || localeMutating
    || destinationSaving
    || Boolean(mediaMutatingSlot)
    || localizationBusy
    || localizationActionBusy
    || localizationRunning;
  const visibleCopyFields = useMemo(
    () => COPY_FIELDS.filter(({ key }) => key === 'headline'
      || key === 'primaryText'
      || Boolean(editableItem?.localizedCopy?.[key])),
    [editableItem],
  );
  const localeConfigReadyForCreative = Boolean(editableItem
    && !localesLoading
    && projectLocaleConfigStored !== false
    && projectLocales.some((locale) => locale.code === editableItem.defaultLanguage)
    && editableItem.languages.every((language) => projectLocales.some((locale) => locale.code === language)));
  const localeOptions = useMemo(() => {
    if (!editableItem || !localeConfigReadyForCreative) return [];
    return projectLocales.filter((locale) => (
      !findCreativeLocaleKey(editableItem, locale.code)
      || getCreativeLocaleStatus(editableItem, locale.code) === 'archived'
    ));
  }, [editableItem, localeConfigReadyForCreative, projectLocales]);
  const defaultLocaleCandidateComplete = useMemo(() => {
    if (!editableItem || !selectedLocale || projection?.isSource || projection?.status === 'archived') return false;
    const exactCopy = getExactCreativeLocaleCopy(editableItem, selectedLocale);
    const exactMediaComplete = Object.values(editableItem.localizedMedia ?? {}).every((slot) => {
      const entry = getExactMediaEntry(slot.translations ?? {}, selectedLocale);
      // Template-required slots are authoritative on the server. The drawer
      // does not own that catalog, so it conservatively treats every known
      // slot as required and never presents a disabled entry as source-ready.
      return Boolean(entry?.disabled !== true && entry?.asset?.assetId?.trim());
    });
    return Object.prototype.hasOwnProperty.call(exactCopy, 'headline')
      && Object.prototype.hasOwnProperty.call(exactCopy, 'primaryText')
      && Object.prototype.hasOwnProperty.call(editableItem.localizedDestinations ?? {}, selectedLocale)
      && exactMediaComplete;
  }, [editableItem, projection?.isSource, projection?.status, selectedLocale]);

  const getFreshLocalizationManifest = async (): Promise<AdCreativeLocalizationManifest> => {
    if (!editableItem) throw new Error('No editable Segmently creative is selected.');
    const key = `${projectId}:${editableItem.id}:${editableItem.sourceRevision}`;
    if (localizationManifestCacheRef.current?.key === key) {
      return localizationManifestCacheRef.current.manifest;
    }
    if (localizationManifestRequestRef.current?.key === key) {
      return localizationManifestRequestRef.current.promise;
    }
    const promise = (async () => {
      const manifestResponse = await client.exportItemLocalizationManifest(
        projectId,
        editableItem.id,
        editableItem.defaultLanguage,
      );
      const manifest = manifestResponse.data?.manifest;
      if (!manifestResponse.success || !manifest) {
        throw new Error(manifestResponse.error || 'Failed to prepare localization manifest');
      }
      localizationManifestCacheRef.current = { key, manifest };
      setCopyFieldConstraints(copyConstraintsFromManifest(manifest));
      return manifest;
    })();
    localizationManifestRequestRef.current = { key, promise };
    try {
      return await promise;
    } finally {
      if (localizationManifestRequestRef.current?.promise === promise) {
        localizationManifestRequestRef.current = null;
      }
    }
  };

  const applyFreshSingleOperation = async (
    entryPath: string,
    operation: Omit<AdCreativeLocalizationOperation, 'entryId'>,
  ): Promise<void> => {
    if (!editableItem || !selectedLocale) return;
    const manifest = await getFreshLocalizationManifest();
    const entry = manifest.entries.find((candidate) => candidate.path === entryPath);
    if (!entry) {
      throw new Error(`The source manifest does not contain ${entryPath}.`);
    }
    const operations: AdCreativeLocalizationOperation[] = [{ entryId: entry.entryId, ...operation }];
    const dryRun = await client.applyItemLocalization(
      projectId,
      editableItem.id,
      selectedLocale,
      manifest,
      operations,
      { dryRun: true },
    );
    if (!dryRun.success || !dryRun.data?.result) {
      throw new Error(dryRun.error || 'Localization validation failed');
    }
    const applied = await client.applyItemLocalization(
      projectId,
      editableItem.id,
      selectedLocale,
      manifest,
      operations,
    );
    if (!applied.success || !applied.data?.result) {
      throw new Error(applied.error || 'Failed to apply localization change');
    }
    localizationManifestCacheRef.current = null;
    localizationManifestRequestRef.current = null;
    await onRefreshItem(editableItem.id);
  };

  const setMutationSuccess = (message: string) => {
    setSaveStatus(projection?.isSource
      ? `${message} Source revision changed; only dependent target components are stale and must be reviewed again.`
      : `${message} ${selectedLocale} remains review-gated until approval.`);
  };

  const mutateMedia = async (
    slotKey: string,
    operation: Omit<AdCreativeLocalizationOperation, 'entryId'>,
    successMessage: string,
  ) => {
    if (!editableItem || !selectedLocale) return;
    // Reusing the source inside the source locale is a semantic no-op. Keep the
    // guard in the action layer as well as the disabled button so a synthetic
    // click cannot create a revision and incorrectly stale every target.
    if (operation.action === 'reuse_source' && projection?.isSource) return;
    setMediaMutatingSlot(slotKey);
    setError(null);
    setSaveStatus(null);
    try {
      await applyFreshSingleOperation(`localizedMedia.${slotKey}`, operation);
      setMutationSuccess(successMessage);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setMediaMutatingSlot(null);
    }
  };

  const reuseSourceMediaForAllLocales = async (slotKey: string) => {
    if (!editableItem || !projection?.isSource) return;
    setMediaMutatingSlot(slotKey);
    setError(null);
    setSaveStatus(null);
    try {
      const response = await client.reuseItemSourceMediaForAllLocales(
        projectId,
        editableItem.id,
        slotKey,
      );
      if (!response.success || !response.data?.item) {
        throw new Error(response.error || `Failed to reuse ${slotKey} for every active language.`);
      }
      onItemUpdated(response.data.item);
      const changed = response.data.changedLocales.length;
      setSaveStatus(changed === 0
        ? `${slotKey} already uses the exact source asset in every active language.`
        : `${slotKey} now uses one exact Segmently asset in ${response.data.appliedLocales.length} active languages. ${changed} target language${changed === 1 ? '' : 's'} returned to review. Meta/Facebook was not changed.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setMediaMutatingSlot(null);
    }
  };

  const uploadMedia = async (slotKey: string, mediaKind: string, file: File) => {
    if (!editableItem || !selectedLocale) return;
    const isImage = mediaKind === 'image' || mediaKind === 'poster';
    const isVideo = mediaKind === 'video';
    if (!isImage && !isVideo) {
      setError(`Manual upload is not supported for the ${mediaKind} slot type.`);
      return;
    }
    if (isImage && !['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError(`Choose a JPEG, PNG, or WebP image for the ${slotKey} slot.`);
      return;
    }
    if (isVideo && !['video/mp4', 'video/webm'].includes(file.type)) {
      setError(`Choose an MP4 or WebM video for the ${slotKey} slot.`);
      return;
    }
    if (isVideo && file.size > MANUAL_LOCALIZED_VIDEO_STAGED_MAX_BYTES) {
      setError('Manual localized videos can be up to 128 MB.');
      return;
    }
    setMediaMutatingSlot(slotKey);
    setError(null);
    setSaveStatus(null);
    try {
      const uploaded = isVideo
        ? file.size > MANUAL_LOCALIZED_VIDEO_MAX_BYTES
          ? await client.uploadItemLocalizationVideoStaged(
            projectId,
            editableItem.id,
            selectedLocale,
            slotKey,
            file,
            (progress) => setManualVideoUploadProgress({ slotKey, ...progress }),
          )
          : await client.uploadItemLocalizationVideo(
            projectId,
            editableItem.id,
            selectedLocale,
            slotKey,
            file,
          )
        : await client.uploadItemLocalizationImage(
          projectId,
          editableItem.id,
          selectedLocale,
          slotKey,
          file,
        );
      if (!uploaded.success || !uploaded.data) {
        throw new Error(uploaded.error || `Failed to upload localized ${isVideo ? 'video' : 'image'}.`);
      }
      await applyFreshSingleOperation(`localizedMedia.${slotKey}`, {
        action: 'set',
        value: {
          ...uploaded.data.asset,
          evidenceId: uploaded.data.evidenceId,
          byteSize: uploaded.data.byteSize,
        },
      });
      setManualMediaEvidence((current) => ({
        ...current,
        [`${selectedLocale}:${slotKey}`]: {
          asset: uploaded.data!.asset,
          evidenceId: uploaded.data!.evidenceId,
          byteSize: uploaded.data!.byteSize,
          objectGeneration: 'objectGeneration' in uploaded.data!
            ? uploaded.data!.objectGeneration
            : undefined,
          mediaKind: uploaded.data!.mediaKind,
        },
      }));
      setMutationSuccess(`${isVideo ? 'Video' : 'Image'} uploaded for ${slotKey}.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setMediaMutatingSlot(null);
      setManualVideoUploadProgress(null);
    }
  };

  const saveDestination = async () => {
    if (!editableItem || !selectedLocale || !destinationTouched) return;
    setDestinationSaving(true);
    setError(null);
    setSaveStatus(null);
    try {
      const destinationValue = destinationValueFromDraft({
        mode: destinationMode,
        url: destinationUrl,
        option: selectedDestinationOption,
      });
      const operation: Omit<AdCreativeLocalizationOperation, 'entryId'> = destinationMode === 'none'
        ? { action: 'clear' }
        : { action: 'set', value: destinationValue };
      await applyFreshSingleOperation('destination', operation);
      setDestinationTouched(false);
      setDestinationFallbackApproved(false);
      setMutationSuccess(destinationMode === 'none'
        ? 'Destination saved as explicit none.'
        : `Exact ${destinationMode === 'funnel' ? 'funnel' : destinationMode} destination saved. Review and approval are still required.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setDestinationSaving(false);
    }
  };

  const saveCopy = async () => {
    if (!editableItem || !selectedLocale || touchedFields.size === 0) return;
    setSaving(true);
    setError(null);
    setSaveStatus(null);
    try {
      const manifest = await getFreshLocalizationManifest();
      const operations: AdCreativeLocalizationOperation[] = Array.from(touchedFields).map((field) => {
        const entry = manifest.entries.find((candidate) => candidate.path === `localizedCopy.${field}`);
        if (!entry) throw new Error(`The field ${field} is not present in the current localization manifest.`);
        return { entryId: entry.entryId, action: 'set', value: copyDraft[field] ?? '' };
      });
      const dryRun = await client.applyItemLocalization(
        projectId,
        editableItem.id,
        selectedLocale,
        manifest,
        operations,
        { dryRun: true },
      );
      if (!dryRun.success || !dryRun.data?.result) {
        throw new Error(dryRun.error || 'Localization validation failed');
      }
      const applied = await client.applyItemLocalization(
        projectId,
        editableItem.id,
        selectedLocale,
        manifest,
        operations,
      );
      if (!applied.success || !applied.data?.result) {
        throw new Error(applied.error || 'Failed to save localized copy');
      }
      localizationManifestCacheRef.current = null;
      localizationManifestRequestRef.current = null;
      setTouchedFields(new Set());
      setSaveStatus(projection?.isSource
        ? `${operations.length} source field${operations.length === 1 ? '' : 's'} validated and saved for ${selectedLocale}. Only dependent target components were marked stale for review.`
        : `${operations.length} field${operations.length === 1 ? '' : 's'} validated and saved for ${selectedLocale}. Review is still required.`);
      await onRefreshItem(editableItem.id);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setSaving(false);
    }
  };

  const addOrRestoreLocale = async () => {
    if (!editableItem || !newLocale) return;
    setLocaleMutating(true);
    setError(null);
    try {
      const response = await client.addOrRestoreItemLocale(
        projectId,
        editableItem.id,
        newLocale,
        editableItem.defaultLanguage,
      );
      if (!response.success || !response.data?.item) {
        throw new Error(response.error || 'Failed to add the language');
      }
      onItemUpdated(response.data.item);
      setSelectedLocale(newLocale);
      setSaveStatus(response.data.restored ? `${newLocale} restored with its previous values.` : `${newLocale} added as an empty manual draft.`);
      setShowAddLocale(false);
      setNewLocale('');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setLocaleMutating(false);
    }
  };

  const archiveLocale = async () => {
    if (!editableItem || !selectedLocale || projection?.isSource) return;
    if (typeof window !== 'undefined' && !window.confirm(`Archive ${selectedLocale}? Its values and assets will be preserved.`)) return;
    setLocaleMutating(true);
    setError(null);
    try {
      const response = await client.archiveItemLocale(projectId, editableItem.id, selectedLocale);
      if (!response.success || !response.data?.item) {
        throw new Error(response.error || 'Failed to archive the language');
      }
      onItemUpdated(response.data.item);
      setSelectedLocale(response.data.item.defaultLanguage);
      setSaveStatus(`${selectedLocale} archived. Its values can be restored later.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setLocaleMutating(false);
    }
  };

  const changeDefaultLocale = async () => {
    if (!editableItem || !selectedLocale || !defaultLocaleCandidateComplete || !defaultLocaleConfirmationOpen) return;
    setLocaleMutating(true);
    setError(null);
    setSaveStatus(null);
    try {
      const response = await client.changeItemDefaultLocale(projectId, editableItem.id, selectedLocale);
      if (!response.success || !response.data?.item) {
        throw new Error(response.error || 'Failed to change the default language');
      }
      const nextDefault = response.data.item.defaultLanguage;
      onItemUpdated(response.data.item);
      setSelectedLocale(nextDefault);
      setDefaultLocaleConfirmationOpen(false);
      setSaveStatus(`${nextDefault} is now the source/default language. Every other active language returned to review. Meta/Facebook was not changed.`);
      await onRefreshItem(response.data.item.id);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setLocaleMutating(false);
    }
  };

  const approveLocale = async () => {
    if (!editableItem || !selectedLocale || projection?.isSource) return;
    setLocaleMutating(true);
    setError(null);
    try {
      const response = await client.reviewItemLocalization(projectId, editableItem.id, selectedLocale, {
        approvalStage: 'approved',
      });
      if (!response.success || !response.data?.item) {
        throw new Error(response.error || 'Failed to approve the locale');
      }
      onItemUpdated(response.data.item);
      setSaveStatus(`${selectedLocale} approved. This does not publish or change Meta.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setLocaleMutating(false);
    }
  };

  const reviewLocaleComponent = async (
    component: 'text' | 'images' | 'video' | 'destination',
    approvalStage: 'approved' | 'rejected' | 'changes_requested',
    reconcileStale = false,
  ) => {
    if (!editableItem || !selectedLocale || projection?.isSource) return;
    if (component === 'images'
      && approvalStage === 'approved'
      && requiresAiImageReview
      && !imageReviewGate.canAccept) {
      setError(imageReviewGate.reason || 'Current passing AI image evidence is required before accepting images.');
      return;
    }
    if (component === 'video'
      && approvalStage === 'approved'
      && requiresAiVideoReview
      && !videoReviewGate.canAccept) {
      setError(videoReviewGate.reason || 'Load and verify the exact evidence-bound localized video before accepting it.');
      return;
    }
    setLocaleMutating(true);
    setError(null);
    try {
      const response = await client.reviewItemLocalization(projectId, editableItem.id, selectedLocale, {
        approvalStage,
        component,
        reconcileStale,
        notes: componentReviewNotes.trim() || undefined,
        requestedChanges: approvalStage === 'changes_requested' && componentReviewNotes.trim()
          ? [componentReviewNotes.trim()]
          : undefined,
      });
      if (!response.success || !response.data?.item || !response.data.variant) {
        throw new Error(response.error || `Failed to review ${component}.`);
      }
      onItemUpdated(response.data.item);
      setLocalizationIndex((current) => current ? {
        ...current,
        localeRevision: response.data!.item.localeRevision,
        statusByLocale: response.data!.item.statusByLocale,
        variants: current.variants.map((variant) => (
          variant.id === response.data!.variant.id ? response.data!.variant : variant
        )),
      } : current);
      setComponentReviewNotes('');
      const action = reconcileStale
        ? 'accepted against the current source revision'
        : approvalStage === 'approved'
          ? 'accepted'
          : approvalStage === 'rejected'
            ? 'rejected'
            : 'returned with requested changes';
      setSaveStatus(`${componentLabel(component)} ${action}. Meta/Facebook source data was not changed.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setLocaleMutating(false);
    }
  };

  const prepareComponentRegeneration = (
    component: 'text' | 'images' | 'video' | 'destination',
  ) => {
    if (component === 'destination') {
      setSaveStatus('Edit the destination below, then review the destination component again.');
      return;
    }
    setLocalizationComponent(component);
    setLocalizationPreflight(null);
    setLocalizationJob(null);
    setLocalizationConfirmed(false);
    setLocalizationError(null);
    setSaveStatus(`AI ${componentLabel(component).toLowerCase()} regeneration is ready for a fresh capability and cost preflight.`);
  };

  const validate = async () => {
    if (!editableItem || !selectedLocale) return;
    setValidating(true);
    setError(null);
    try {
      const response = destinationFallbackApproved
        ? await client.validateItem(projectId, editableItem.id, selectedLocale, { fallbackApproved: true })
        : await client.validateItem(projectId, editableItem.id, selectedLocale);
      if (!response.success) throw new Error(response.error || 'Validation failed to run');
      await onRefreshItem(editableItem.id);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setValidating(false);
    }
  };

  const runAction = async (action: (item: AdCreativeItem) => Promise<void> | void) => {
    if (!editableItem) return;
    setActionRunning(true);
    setError(null);
    try {
      await action(editableItem);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setActionRunning(false);
    }
  };

  const runImageSourceInspection = async () => {
    if (!editableItem
      || !selectedLocale
      || projection?.isSource
      || localizationComponent !== 'images') return;
    const inspectionScope = sourceImageInspectionScope;
    setImageInspectionBusy(true);
    setLocalizationError(null);
    setLocalizationPreflight(null);
    setLocalizationJob(null);
    setLocalizationConfirmed(false);
    try {
      if (sourceImageSlotKeys.length === 0) {
        throw new Error('This creative has no source image slots to inspect.');
      }
      const inspected = await client.inspectItemLocalizationSourceImages({
        projectId,
        itemId: editableItem.id,
        slotKeys: sourceImageSlotKeys,
      });
      if (inspectionScope !== imageInspectionScopeRef.current) return;
      if (!inspected.success || !inspected.data?.evidence) {
        throw new Error(localizationResponseError(inspected, 'Failed to inspect the selected source images.'));
      }
      if (inspected.data.sourceRevision !== editableItem.sourceRevision) {
        throw new Error('The source creative changed while its images were being inspected. Refresh and try again.');
      }
      setImageSourceEvidence(inspected.data.evidence);
      setImageTargetVisibleText({});
    } catch (nextError) {
      if (inspectionScope === imageInspectionScopeRef.current) {
        setLocalizationError(nextError instanceof Error ? nextError.message : String(nextError));
      }
    } finally {
      if (inspectionScope === imageInspectionScopeRef.current) setImageInspectionBusy(false);
    }
  };

  const runLocalizationPreflight = async () => {
    if (!editableItem || !selectedLocale || projection?.isSource) return;
    const runKey = localizationRunKeyRef.current;
    setLocalizationBusy(true);
    setLocalizationError(null);
    setLocalizationPreflight(null);
    setLocalizationJob(null);
    setLocalizationConfirmed(false);
    try {
      const resolvedEvidence = imageSourceEvidence;
      if (localizationComponent === 'images') {
        if (sourceImageSlotKeys.length === 0) throw new Error('This creative has no source image slots to localize.');
        const evidenceCurrent = sourceImageSlotKeys.every((slotKey) => (
          resolvedEvidence[slotKey]?.sourceRevision === editableItem.sourceRevision
        ));
        if (!evidenceCurrent) {
          throw new Error(
            'Inspect the exact current source images first. Image inspection is a separate action that may use AI credits.',
          );
        }
        const missingTargetText = sourceImageSlotKeys.filter((slotKey) => (
          resolvedEvidence[slotKey]?.visibleText.state === 'detected'
          && splitExactLines(imageTargetVisibleText[slotKey] ?? '').length === 0
        ));
        if (missingTargetText.length > 0) {
          throw new Error(`Enter exact ${selectedLocale} visible text for: ${missingTargetText.join(', ')}. Segmently will not invent image copy.`);
        }
      }
      const request = buildDrawerLocalizationPreflightRequest({
        projectId,
        item: editableItem,
        targetLocale: selectedLocale,
        component: localizationComponent,
        imageMode: imageLocalizationMode,
        imageSlotKeys: sourceImageSlotKeys,
        imageEvidence: resolvedEvidence,
        imageTargetVisibleText,
        videoSlotKey,
        videoVoiceEnabled,
        videoVoiceMode,
        videoVoiceRightsSelection,
        approvedAudioArtifactId: selectedApprovedAudioArtifactId,
        videoOnScreenTextEnabled,
        videoTextInspectionSelection,
        videoCulturalVisualEnabled,
        culturalVisualSelection,
        localeProfileId: selectedLocaleProfile?.id,
        glossaryRef: selectedLocaleGlossary?.id,
        textContext: {
          targetMarket: localizationTargetMarket,
          tone: localizationTone,
          marketDirection: localizationMarketDirection,
          glossary: localizationGlossary,
          claims: localizationClaims,
          protectedTokens: localizationProtectedTokens,
          userInstruction: localizationInstruction,
        },
      });
      if (!request) throw new Error('This creative has no source media slot to localize.');
      const response = await client.preflightItemLocalization(request);
      if (runKey !== localizationRunKeyRef.current) return;
      if (!response.success || !response.data?.preflight) {
        throw new Error(localizationResponseError(
          response,
          `Failed to preflight ${localizationComponent} localization.`,
        ));
      }
      if (response.data.component !== localizationComponent) {
        throw new Error('Localization preflight returned a different component than requested.');
      }
      setLocalizationPreflight(response.data.preflight);
    } catch (nextError) {
      if (runKey === localizationRunKeyRef.current) {
        setLocalizationError(nextError instanceof Error ? nextError.message : String(nextError));
      }
    } finally {
      if (runKey === localizationRunKeyRef.current) setLocalizationBusy(false);
    }
  };

  const startLocalizationJob = async () => {
    if (!editableItem
      || !selectedLocale
      || projection?.isSource
      || !localizationPreflight
      || localizationPreflight.component !== localizationComponent
      || !localizationConfirmed
      || !isLocalizationPreflightSupported(localizationPreflight)) return;
    const request = buildDrawerLocalizationStartRequest({
      projectId,
      itemId: editableItem.id,
      preflight: localizationPreflight,
      localeProfileId: selectedLocaleProfile?.id,
      glossaryRef: selectedLocaleGlossary?.id,
    });
    const runKey = localizationRunKeyRef.current;
    setLocalizationBusy(true);
    setLocalizationError(null);
    try {
      const response = await client.startItemLocalizationJob(request);
      if (runKey !== localizationRunKeyRef.current) return;
      if (!response.success || !response.data?.job) {
        throw new Error(localizationResponseError(
          response,
          `Failed to start ${localizationComponent} localization.`,
        ));
      }
      if (response.data.component !== localizationComponent) {
        throw new Error('Localization job returned a different component than requested.');
      }
      const snapshot = localizationStartSnapshot(response.data);
      setLocalizationJob(snapshot);
      setLocalizationConfirmed(false);
      if (isLocalizationSnapshotTerminal(snapshot)) {
        await onRefreshItem(editableItem.id);
      }
    } catch (nextError) {
      if (runKey === localizationRunKeyRef.current) {
        setLocalizationError(nextError instanceof Error ? nextError.message : String(nextError));
      }
    } finally {
      if (runKey === localizationRunKeyRef.current) setLocalizationBusy(false);
    }
  };

  const refreshLocalizationJob = useCallback(async (): Promise<void> => {
    if (!editableItem || !localizationJob) return;
    const runKey = localizationRunKeyRef.current;
    const jobId = localizationJob.job.id;
    const component = localizationJob.component;
    try {
      const response: AdCreativeLocalizationJobResponse = await client.getLocalizationJob(
        projectId,
        jobId,
        component,
      );
      if (runKey !== localizationRunKeyRef.current) return;
      if (!response.success || !response.data?.job) {
        throw new Error(localizationResponseError(response, 'Failed to refresh localization progress.'));
      }
      if (response.data.component !== component) {
        throw new Error('Localization progress returned a different component than requested.');
      }
      const snapshot = localizationJobSnapshot(response.data);
      const mergedSnapshot = snapshot.task
        ? snapshot
        : { ...snapshot, task: localizationJob.task };
      setLocalizationJob(mergedSnapshot);
      setLocalizationError(null);
      if (isLocalizationSnapshotTerminal(mergedSnapshot)) {
        await onRefreshItem(editableItem.id);
      }
    } catch (nextError) {
      if (runKey === localizationRunKeyRef.current) {
        setLocalizationError(nextError instanceof Error ? nextError.message : String(nextError));
      }
    }
  }, [client, editableItem, localizationJob, onRefreshItem, projectId]);

  useTaskTransition({
    filter: (task) => Boolean(localizationJob?.task?.id && task.id === localizationJob.task.id),
    onTransition: () => {
      void refreshLocalizationJob();
    },
  });

  useEffect(() => {
    if (!localizationJob || isLocalizationSnapshotTerminal(localizationJob)) return undefined;
    const interval = window.setInterval(() => {
      void refreshLocalizationJob();
    }, LOCALIZATION_SAFETY_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [localizationJob, refreshLocalizationJob]);

  const mutateLocalizationJob = async (action: 'cancel' | 'resume'): Promise<void> => {
    if (!localizationJob) return;
    const runKey = localizationRunKeyRef.current;
    setLocalizationActionBusy(true);
    setLocalizationError(null);
    try {
      const response = action === 'cancel'
        ? await client.cancelLocalizationJob(
          projectId,
          localizationJob.job.id,
          localizationJob.component,
        )
        : await client.resumeLocalizationJob(
          projectId,
          localizationJob.job.id,
          localizationJob.component,
        );
      if (runKey !== localizationRunKeyRef.current) return;
      if (!response.success || !response.data?.job) {
        throw new Error(localizationResponseError(
          response,
          `Failed to ${action} localization.`,
        ));
      }
      if (response.data.component !== localizationJob.component) {
        throw new Error(`Localization ${action} returned a different component than requested.`);
      }
      const snapshot = localizationJobSnapshot(response.data);
      setLocalizationJob(snapshot.task
        ? snapshot
        : { ...snapshot, task: localizationJob.task });
      if (action === 'resume') setLocalizationConfirmed(false);
    } catch (nextError) {
      if (runKey === localizationRunKeyRef.current) {
        setLocalizationError(nextError instanceof Error ? nextError.message : String(nextError));
      }
    } finally {
      if (runKey === localizationRunKeyRef.current) setLocalizationActionBusy(false);
    }
  };

  const loadCaptionReview = useCallback(async (): Promise<void> => {
    const jobId = videoReviewSlotBinding?.jobId;
    if (!jobId || !selectedLocale || !videoSlotKey || !editableItem?.id) return;
    const requestScope = videoReviewBindingScope;
    captionReviewRequestRef.current = requestScope;
    setCaptionReviewLoading(true);
    setCaptionReviewError(null);
    setVideoReviewGate({
      canAccept: false,
      reason: 'The exact localized derivative has not loaded yet.',
    });
    try {
      const response = await client.getCaptionReview(projectId, jobId, selectedLocale, videoSlotKey);
      if (requestScope !== captionReviewRequestRef.current) return;
      if (!response.success || !response.data?.review) {
        throw new Error(localizationResponseError(response, 'Failed to load caption review.'));
      }
      const review = response.data.review;
      if (review.jobId !== jobId
        || review.creativeItemId !== editableItem?.id
        || review.targetLocale !== selectedLocale
        || review.slotKey !== videoSlotKey
        || review.reviewStatus !== 'review_required'
        || typeof review.sidecarAvailable !== 'boolean'
        || !review.renderedDerivative
        || !review.verificationEvidence
        || !Array.isArray(review.verificationEvidence.checks)) {
        throw new Error('Video review returned mismatched immutable lineage.');
      }
      setCaptionReview(review);
    } catch (nextError) {
      if (requestScope === captionReviewRequestRef.current) {
        setCaptionReviewError(nextError instanceof Error ? nextError.message : String(nextError));
      }
    } finally {
      if (requestScope === captionReviewRequestRef.current) setCaptionReviewLoading(false);
    }
  }, [
    client,
    editableItem?.id,
    projectId,
    selectedLocale,
    videoReviewBindingScope,
    videoReviewSlotBinding?.jobId,
    videoSlotKey,
  ]);

  useEffect(() => {
    if (!requiresAiVideoReview || !videoReviewSlotBinding) return;
    void loadCaptionReview();
  }, [loadCaptionReview, requiresAiVideoReview, videoReviewBindingScope, videoReviewSlotBinding]);

  const verifyLocalizedVideoPlayback = (event: React.SyntheticEvent<HTMLVideoElement>): void => {
    const media = event.currentTarget;
    if (!captionPlaybackSource || !videoReviewSlotBinding) {
      setVideoReviewGate({ canAccept: false, reason: 'Immutable video binding is missing or stale.' });
      return;
    }
    const loadedDurationMs = Number.isFinite(media.duration)
      ? Math.round(media.duration * 1000)
      : Number.NaN;
    const expectedUrl = stableSegmentlyReviewAssetUrl(captionPlaybackSource.videoUrl);
    const actualUrl = stableSegmentlyReviewAssetUrl(media.currentSrc);
    if (actualUrl !== expectedUrl
      || media.videoWidth !== videoReviewSlotBinding.outputWidth
      || media.videoHeight !== videoReviewSlotBinding.outputHeight
      || loadedDurationMs !== videoReviewSlotBinding.outputDurationMs) {
      setVideoReviewGate({
        canAccept: false,
        reason: 'Loaded playback metadata does not match the immutable localized-video binding.',
      });
      return;
    }
    setVideoReviewGate({
      canAccept: true,
      reason: 'The exact evidence-bound localized derivative loaded successfully.',
    });
  };

  const rejectLocalizedVideoPlayback = (): void => {
    setVideoReviewGate({
      canAccept: false,
      reason: 'The exact localized derivative could not be loaded.',
    });
  };

  const experiments = useMemo(() => collectExperiments(editableItem), [editableItem]);
  const promptExperiment = experiments.find((entry) => entry.experiment.promptTemplate);
  const activeLocalizationPreflight = localizationPreflight?.component === localizationComponent
    ? localizationPreflight
    : null;
  const localizationUnsupportedReasons = activeLocalizationPreflight
    ? collectLocalizationUnsupportedReasons(activeLocalizationPreflight)
    : [];
  const localizationSupported = Boolean(activeLocalizationPreflight
    && isLocalizationPreflightSupported(activeLocalizationPreflight));
  const requiredVideoVoiceOperation = videoVoiceMode === 'replace_with_tts' ? 'replace' : 'dub';
  const videoVoiceSelectionCompatible = Boolean(videoVoiceRightsSelection
    && videoVoiceRightsSelection.allowedOperations.includes(requiredVideoVoiceOperation));
  const imageInspectionCurrent = Boolean(editableItem
    && sourceImageSlotKeys.length > 0
    && sourceImageSlotKeys.every((slotKey) => (
      imageSourceEvidence[slotKey]?.sourceRevision === editableItem.sourceRevision
    )));
  const localizationClientBlocker = !localeConfigReadyForCreative
    ? 'Project locale settings are required before this creative can be localized.'
    : localizationComponent === 'text' && localeAuthoring.loading
      ? 'Loading saved localization profiles and glossaries.'
      : localizationComponent === 'images' && sourceImageSlotKeys.length === 0
      ? 'This creative has no exact source image slots.'
      : localizationComponent === 'images' && !imageInspectionCurrent
        ? 'Inspect the exact current source images before checking localization capability. This is a separate action that may use AI credits.'
      : localizationComponent === 'video' && !videoSlotKey
        ? 'This creative has no exact source video slot.'
        : localizationComponent === 'video' && videoOnScreenTextEnabled && !videoTextInspectionSelection
          ? 'Play the exact source video, save its visible-text map, and enter every target replacement.'
          : localizationComponent === 'video' && videoOnScreenTextEnabled
            && Boolean(videoTextInspectionSelection?.replacements.some((replacement) => !replacement.targetText.trim()))
            ? `Enter exact ${selectedLocale} replacement text for every mapped video region.`
          : localizationComponent === 'video' && videoCulturalVisualEnabled && !culturalVisualSelection
            ? 'Play the exact source video, select bounded scenes and protected source images, then save the review.'
        : localizationComponent === 'video' && videoVoiceEnabled && !videoVoiceRightsSelection
          ? 'Select or record an active voice-rights approval for this exact Segmently video and target language.'
          : localizationComponent === 'video' && videoVoiceEnabled && videoVoiceMode !== 'replace_with_tts'
            ? 'Dubbing is not available yet. Use exact full-track replacement with approved localized audio.'
          : localizationComponent === 'video' && videoVoiceEnabled && !videoVoiceSelectionCompatible
            ? `The selected voice approval does not allow the ${requiredVideoVoiceOperation} operation.`
          : localizationComponent === 'video' && videoVoiceEnabled && !exactTargetCaptionArtifactId
            ? 'Create the caption-only localized video first. Approved audio must be reviewed against its exact translated caption artifact.'
          : localizationComponent === 'video' && videoVoiceEnabled && !selectedApprovedAudioArtifactId
            ? 'Upload, listen to, approve, and select localized audio for these exact captions and voice rights.'
        : null;
  const selectedLocaleDirection = localeDirection(selectedLocale);
  const sourceLocale = sourceProjection?.sourceLocale ?? editableItem?.defaultLanguage ?? selectedLocale;
  const sourceLocaleDirection = localeDirection(sourceLocale);

  return (
    <Drawer
      variant={isPersistent ? 'persistent' : 'temporary'}
      anchor="right"
      open={Boolean(editableItem)}
      onClose={onClose}
      data-testid={AD_CREATIVE_BATCH.drawer}
      sx={{
        width: editableItem ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          maxWidth: '100vw',
          boxSizing: 'border-box',
          position: isPersistent ? 'relative' : 'fixed',
          borderLeft: 1,
          borderColor: 'divider',
        },
      }}
    >
      {editableItem && projection && sourceProjection && (
        // overflowX clamp: a wide child (e.g. a fixed-min controls row) must
        // scroll/wrap INSIDE the 420px drawer, never widen the page.
        <Stack
          spacing={1.5}
          lang={selectedLocale || undefined}
          dir={selectedLocaleDirection}
          data-locale-direction={selectedLocaleDirection}
          sx={{ p: 2, overflowY: 'auto', overflowX: 'hidden', minWidth: 0 }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap title={localizedTitle}>
              {localizedTitle}
            </Typography>
            <IconButton
              size="small"
              aria-label={`Close ${localizedTitle || 'creative details'}`}
              data-testid={AD_CREATIVE_BATCH.drawerClose}
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Box
            data-testid={AD_CREATIVE_BATCH.drawerLocales}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25 }}
          >
            <Stack spacing={1}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <StyledTextField
                  select
                  label="Language"
                  size="small"
                  fullWidth
                  value={selectedLocale}
                  disabled={localesLoading || contentMutating || copyChanged || destinationTouched}
                  data-testid={AD_CREATIVE_BATCH.drawerLocaleSelect}
                  onChange={(value) => setSelectedLocale(String(value))}
                >
                  {editableItem.languages.map((locale) => (
                    <MenuItem key={locale} value={locale}>
                      {projectLocales.find((candidate) => candidate.code === locale)?.label ?? locale}
                      {locale === editableItem.defaultLanguage ? ' · source/default' : ` · ${getCreativeLocaleStatus(editableItem, locale)}`}
                    </MenuItem>
                  ))}
                </StyledTextField>
                <Chip
                  size="small"
                  color={localeStatusColor(projection.status)}
                  label={projection.isSource ? 'source' : projection.status.replace(/_/g, ' ')}
                  data-testid={AD_CREATIVE_BATCH.drawerLocaleStatus}
                />
              </Stack>
              {!localesLoading && !localeConfigReadyForCreative && (
                <Alert severity="warning" data-testid={AD_CREATIVE_BATCH.drawerLocaleConfigRequired}>
                  Project locale settings are missing or do not cover this creative. Configure project languages before adding or generating localized content.
                </Alert>
              )}
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  disabled={localesLoading || contentMutating || copyChanged || destinationTouched || localeOptions.length === 0}
                  data-testid={AD_CREATIVE_BATCH.drawerAddLocale}
                  onClick={() => {
                    const first = localeOptions[0]?.code ?? '';
                    setNewLocale(first);
                    setShowAddLocale((current) => !current);
                  }}
                >
                  Add language
                </Button>
                {!projection.isSource && (
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<ArchiveIcon />}
                    disabled={contentMutating || copyChanged || destinationTouched}
                    data-testid={AD_CREATIVE_BATCH.drawerArchiveLocale}
                    onClick={() => void archiveLocale()}
                  >
                    Archive
                  </Button>
                )}
                {!projection.isSource && defaultLocaleCandidateComplete && (
                  <Button
                    size="small"
                    color="inherit"
                    disabled={contentMutating || copyChanged || destinationTouched}
                    data-testid={AD_CREATIVE_BATCH.drawerMakeDefaultLocale}
                    onClick={() => setDefaultLocaleConfirmationOpen(true)}
                  >
                    Make default
                  </Button>
                )}
                {!projection.isSource && (
                  <Button
                    size="small"
                    startIcon={<CompareArrowsIcon />}
                    color={compareWithSource ? 'primary' : 'inherit'}
                    data-testid={AD_CREATIVE_BATCH.drawerCompareLocale}
                    onClick={() => setCompareWithSource((current) => !current)}
                  >
                    Compare
                  </Button>
                )}
              </Stack>
              {showAddLocale && (
                <Stack direction="row" spacing={0.75} alignItems="flex-end">
                  <StyledTextField
                    select
                    label="Project language"
                    size="small"
                    fullWidth
                    value={newLocale}
                    data-testid={AD_CREATIVE_BATCH.drawerNewLocaleSelect}
                    onChange={(value) => setNewLocale(String(value))}
                  >
                    {localeOptions.map((locale) => (
                      <MenuItem key={locale.code} value={locale.code}>
                        {locale.label} · {locale.code}
                        {getCreativeLocaleStatus(editableItem, locale.code) === 'archived' ? ' · archived' : ''}
                      </MenuItem>
                    ))}
                  </StyledTextField>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={getCreativeLocaleStatus(editableItem, newLocale) === 'archived' ? <RestoreIcon /> : <AddIcon />}
                    disabled={!newLocale || contentMutating}
                    data-testid={AD_CREATIVE_BATCH.drawerConfirmAddLocale}
                    onClick={() => void addOrRestoreLocale()}
                  >
                    {getCreativeLocaleStatus(editableItem, newLocale) === 'archived' ? 'Restore' : 'Add'}
                  </Button>
                </Stack>
              )}
              {!projection.isSource && !defaultLocaleCandidateComplete && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid={AD_CREATIVE_BATCH.drawerDefaultLocaleIncomplete}
                >
                  Add exact headline, primary text, destination, and media values before making this language the default.
                </Typography>
              )}
              {defaultLocaleConfirmationOpen && defaultLocaleCandidateComplete && (
                <Alert severity="warning" data-testid={AD_CREATIVE_BATCH.drawerDefaultLocaleConfirmation}>
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      Make {selectedLocale} the source/default language? Translation, media, and destination keys keep their current locale names; nothing is copied or renamed. Every other active language returns to review. Meta/Facebook is not changed.
                    </Typography>
                    {projection.status === 'stale' && (
                      <Typography
                        variant="caption"
                        data-testid={AD_CREATIVE_BATCH.drawerDefaultLocaleStaleExplanation}
                      >
                        This language is stale only against the previous source. Its exact saved values will become the new source, while all other languages must be reviewed against it.
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={localeMutating}
                        data-testid={AD_CREATIVE_BATCH.drawerConfirmDefaultLocale}
                        onClick={() => void changeDefaultLocale()}
                      >
                        Confirm new default
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        disabled={localeMutating}
                        data-testid={AD_CREATIVE_BATCH.drawerCancelDefaultLocale}
                        onClick={() => setDefaultLocaleConfirmationOpen(false)}
                      >
                        Keep current default
                      </Button>
                    </Stack>
                  </Stack>
                </Alert>
              )}
              <FormControlLabel
                control={(
                  <Switch
                    size="small"
                    checked={showFallback}
                    onChange={(event) => setShowFallback(event.target.checked)}
                    data-testid={AD_CREATIVE_BATCH.drawerFallbackToggle}
                  />
                )}
                label="Show source fallback in preview"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: 12 } }}
              />
            </Stack>
          </Box>

          {projection.hasAnyFallback && showFallback && (
            <Alert severity="info" data-testid={AD_CREATIVE_BATCH.drawerFallbackNotice}>
              Showing available source-language values where {selectedLocale} is missing. Fallback is preview-only and is never published implicitly.
            </Alert>
          )}

          {projection.isSource && (
            <Alert severity="warning" data-testid={AD_CREATIVE_BATCH.drawerSourceEditWarning}>
              You are editing the source/default language. Saving creates a new source revision. Only target components that depend on the changed source content are marked stale for review.
            </Alert>
          )}

          {!projection.isSource && (
            <Box
              data-testid={AD_CREATIVE_BATCH.drawerAiLocalization}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25, minWidth: 0 }}
            >
              <Stack spacing={1}>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
                    AI localization
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${editableItem.defaultLanguage} → ${selectedLocale}`}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Generate one review-required component at a time. Nothing is approved, published, or sent to Meta automatically.
                </Typography>

                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={localizationComponent}
                  disabled={localizationBusy || localizationRunning}
                  onChange={(_event, next: AdCreativeLocalizationComponent | null) => {
                    if (next) setLocalizationComponent(next);
                  }}
                >
                  {(['text', 'images', 'video'] as AdCreativeLocalizationComponent[]).map((component) => (
                    <ToggleButton
                      key={component}
                      value={component}
                      data-testid={AD_CREATIVE_BATCH.drawerAiComponent(component)}
                      sx={{ minWidth: 0 }}
                    >
                      {component === 'images' ? 'Images' : component[0].toUpperCase() + component.slice(1)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                {localizationComponent === 'text' && (
                  <Accordion
                    disableGutters
                    variant="outlined"
                    data-testid={AD_CREATIVE_BATCH.drawerAiContext}
                    sx={{ '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Market, tone, and terminology</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Optional instructions are frozen into the preflight and job evidence.
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={1}>
                        {localeAuthoring.loading && (
                          <Alert severity="info" data-testid={AD_CREATIVE_BATCH.drawerAiResourcesLoading}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <CircularProgress size={14} />
                              <span>Loading saved localization profiles and glossaries…</span>
                            </Stack>
                          </Alert>
                        )}
                        {localeAuthoring.error && (
                          <Alert
                            severity="error"
                            data-testid={AD_CREATIVE_BATCH.drawerAiResourcesError}
                            action={(
                              <Button
                                size="small"
                                color="inherit"
                                data-testid={AD_CREATIVE_BATCH.drawerAiResourcesRetry}
                                onClick={() => void localeAuthoring.reload()}
                              >
                                Retry
                              </Button>
                            )}
                          >
                            Saved localization guidance could not be loaded: {localeAuthoring.error.message}. Choose no saved resources and continue with inline context, or retry.
                          </Alert>
                        )}
                        {!localeAuthoring.loading
                          && !localeAuthoring.error
                          && compatibleLocaleProfiles.length === 0
                          && compatibleLocaleGlossaries.length === 0 && (
                          <Alert severity="info" data-testid={AD_CREATIVE_BATCH.drawerAiResourcesEmpty}>
                            No active saved profile or glossary exactly matches {editableItem.defaultLanguage} → {selectedLocale}. Inline context below still works.
                          </Alert>
                        )}
                        <StyledTextField
                          select
                          fullWidth
                          size="small"
                          label="Saved localization profile"
                          helperText={`Only active profiles for exact ${editableItem.defaultLanguage} → ${selectedLocale} are shown.`}
                          value={selectedLocaleProfileId}
                          disabled={localeAuthoring.loading || localizationBusy || localizationRunning}
                          data-testid={AD_CREATIVE_BATCH.drawerAiProfile}
                          onChange={(value) => {
                            const profileId = String(value);
                            setSelectedLocaleProfileId(profileId);
                            const profile = compatibleLocaleProfiles.find((candidate) => candidate.id === profileId);
                            const linkedGlossary = profile?.glossaryRef
                              ? compatibleLocaleGlossaries.find((candidate) => candidate.id === profile.glossaryRef)
                              : undefined;
                            if (linkedGlossary) setSelectedLocaleGlossaryId(linkedGlossary.id);
                          }}
                        >
                          <MenuItem value="">No saved profile</MenuItem>
                          {compatibleLocaleProfiles.map((profile, index) => (
                            <MenuItem key={profile.id} value={profile.id}>
                              {localeProfileDisplayLabel(profile, index)}
                            </MenuItem>
                          ))}
                        </StyledTextField>
                        {selectedLocaleProfile && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            data-testid={AD_CREATIVE_BATCH.drawerAiProfileSummary}
                          >
                            {localeProfileSummary(selectedLocaleProfile)} Inline fields below are applied explicitly on top.
                          </Typography>
                        )}
                        <StyledTextField
                          select
                          fullWidth
                          size="small"
                          label="Saved glossary"
                          helperText={linkedCompatibleGlossary?.id === selectedLocaleGlossaryId
                            ? 'Prefilled from the selected profile. You can choose another compatible glossary or none.'
                            : `Optional; only active glossaries for exact ${editableItem.defaultLanguage} → ${selectedLocale} are shown.`}
                          value={selectedLocaleGlossaryId}
                          disabled={localeAuthoring.loading || localizationBusy || localizationRunning}
                          data-testid={AD_CREATIVE_BATCH.drawerAiSavedGlossary}
                          onChange={(value) => setSelectedLocaleGlossaryId(String(value))}
                        >
                          <MenuItem value="">No saved glossary</MenuItem>
                          {compatibleLocaleGlossaries.map((glossary, index) => (
                            <MenuItem key={glossary.id} value={glossary.id}>
                              {localeGlossaryDisplayLabel(glossary, index)}
                            </MenuItem>
                          ))}
                        </StyledTextField>
                        {selectedLocaleGlossary && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            data-testid={AD_CREATIVE_BATCH.drawerAiGlossarySummary}
                          >
                            {localeGlossarySummary(selectedLocaleGlossary)}
                          </Typography>
                        )}
                        <StyledTextField
                          fullWidth
                          size="small"
                          label="Target market"
                          value={localizationTargetMarket}
                          disabled={localizationBusy || localizationRunning}
                          data-testid={AD_CREATIVE_BATCH.drawerAiTargetMarket}
                          onChange={(value) => setLocalizationTargetMarket(String(value))}
                        />
                        <StyledTextField
                          fullWidth
                          size="small"
                          label="Tone of voice"
                          value={localizationTone}
                          disabled={localizationBusy || localizationRunning}
                          data-testid={AD_CREATIVE_BATCH.drawerAiTone}
                          onChange={(value) => setLocalizationTone(String(value))}
                        />
                        <StyledTextField
                          fullWidth
                          multiline
                          minRows={2}
                          size="small"
                          label="Market and cultural direction"
                          value={localizationMarketDirection}
                          disabled={localizationBusy || localizationRunning}
                          data-testid={AD_CREATIVE_BATCH.drawerAiMarketDirection}
                          onChange={(value) => setLocalizationMarketDirection(String(value))}
                        />
                        <StyledTextField
                          fullWidth
                          multiline
                          minRows={3}
                          size="small"
                          label="Additional inline glossary"
                          helperText="Optional mappings added to the selected saved glossary. One per line: source = target"
                          value={localizationGlossary}
                          disabled={localizationBusy || localizationRunning}
                          data-testid={AD_CREATIVE_BATCH.drawerAiGlossary}
                          onChange={(value) => setLocalizationGlossary(String(value))}
                        />
                        <StyledTextField
                          fullWidth
                          multiline
                          minRows={2}
                          size="small"
                          label="Allowed claims and required disclaimers"
                          helperText="One exact phrase per line"
                          value={localizationClaims}
                          disabled={localizationBusy || localizationRunning}
                          data-testid={AD_CREATIVE_BATCH.drawerAiClaims}
                          onChange={(value) => setLocalizationClaims(String(value))}
                        />
                        <StyledTextField
                          fullWidth
                          multiline
                          minRows={2}
                          size="small"
                          label="Protected product names, URLs, and tokens"
                          helperText="One exact value per line"
                          value={localizationProtectedTokens}
                          disabled={localizationBusy || localizationRunning}
                          data-testid={AD_CREATIVE_BATCH.drawerAiProtectedTokens}
                          onChange={(value) => setLocalizationProtectedTokens(String(value))}
                        />
                        <StyledTextField
                          fullWidth
                          multiline
                          minRows={2}
                          size="small"
                          label="Additional instruction"
                          value={localizationInstruction}
                          disabled={localizationBusy || localizationRunning}
                          data-testid={AD_CREATIVE_BATCH.drawerAiInstruction}
                          onChange={(value) => setLocalizationInstruction(String(value))}
                        />
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}

                {localizationComponent === 'images' && (
                  <Stack spacing={1}>
                    <StyledTextField
                      select
                      fullWidth
                      size="small"
                      label="Image treatment"
                      value={imageLocalizationMode}
                      disabled={localizationBusy || localizationRunning}
                      data-testid={AD_CREATIVE_BATCH.drawerAiImageMode}
                      onChange={(value) => setImageLocalizationMode(value as AdCreativeImageLocalizationMode)}
                    >
                      <MenuItem value="reuse_when_language_neutral">Reuse only when verified language-neutral</MenuItem>
                      <MenuItem value="localize_visible_text">Localize verified visible text</MenuItem>
                      <MenuItem value="regenerate">Regenerate with protected references</MenuItem>
                    </StyledTextField>
                    <Alert severity="info">
                      Source inspection is a separate action that may use AI credits. It reads the exact stored source bytes and never edits or publishes anything to Meta.
                    </Alert>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={imageInspectionBusy
                        || localizationBusy
                        || localizationRunning
                        || sourceImageSlotKeys.length === 0}
                      data-testid={AD_CREATIVE_BATCH.drawerAiImageInspect}
                      onClick={() => void runImageSourceInspection()}
                      startIcon={imageInspectionBusy ? <CircularProgress size={14} /> : undefined}
                    >
                      {imageInspectionCurrent ? 'Inspect source images again' : 'Inspect source images (may use AI credits)'}
                    </Button>
                    {imageInspectionBusy && (
                      <Alert severity="info" data-testid={AD_CREATIVE_BATCH.drawerAiImageInspectionProgress}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={14} />
                          <span>Inspecting exact source image bytes and visible text…</span>
                        </Stack>
                      </Alert>
                    )}
                    {imageInspectionCurrent && !imageInspectionBusy && (
                      <Alert severity="success" data-testid={AD_CREATIVE_BATCH.drawerAiImageInspectionReady}>
                        Inspection evidence is ready for this source revision. You can now check localization capability and estimate.
                      </Alert>
                    )}
                    {sourceImageSlotKeys.map((slotKey) => {
                      const evidence = imageSourceEvidence[slotKey];
                      if (evidence?.visibleText.state !== 'detected') return null;
                      return (
                        <StyledTextField
                          key={slotKey}
                          fullWidth
                          multiline
                          minRows={2}
                          size="small"
                          label={`Exact ${selectedLocale} visible text · ${slotKey}`}
                          helperText={`Source OCR: ${(evidence.visibleText.lines ?? []).join(' · ')}. One exact target line per line; no text is invented.`}
                          value={imageTargetVisibleText[slotKey] ?? ''}
                          disabled={localizationBusy || localizationRunning}
                          data-testid={AD_CREATIVE_BATCH.drawerAiImageTargetText(slotKey)}
                          onChange={(value) => setImageTargetVisibleText((current) => ({ ...current, [slotKey]: String(value) }))}
                        />
                      );
                    })}
                  </Stack>
                )}

                {localizationComponent === 'video' && (
                  <Stack spacing={1}>
                    {sourceVideoSlotKeys.length > 1 && (
                      <StyledTextField
                        select
                        fullWidth
                        size="small"
                        label="Source video slot"
                        value={videoSlotKey}
                        disabled={localizationBusy || localizationRunning}
                        data-testid={AD_CREATIVE_BATCH.drawerAiVideoSlot}
                        onChange={(value) => setVideoSlotKey(String(value))}
                      >
                        {sourceVideoSlotKeys.map((slotKey) => (
                          <MenuItem key={slotKey} value={slotKey}>{slotKey}</MenuItem>
                        ))}
                      </StyledTextField>
                    )}
                    <Alert severity="info" data-testid={AD_CREATIVE_BATCH.drawerAiVideoCaptions}>
                      Reviewed captions are always included. They use the selected immutable timed transcript for this exact Segmently-copy video.
                    </Alert>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          size="small"
                          checked={videoVoiceEnabled}
                          disabled={localizationBusy || localizationRunning || !videoSlotKey}
                          data-testid={AD_CREATIVE_BATCH.drawerAiVideoVoiceToggle}
                          onChange={(event) => {
                            setVideoVoiceEnabled(event.target.checked);
                            if (!event.target.checked) {
                              setVideoVoiceRightsSelection(null);
                              setSelectedApprovedAudioArtifactId('');
                            }
                          }}
                        />
                      )}
                      label="Also create a localized voice track"
                      sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { typography: 'body2', pt: 0.35 } }}
                    />
                    {videoVoiceEnabled && videoSlotKey && (
                      <>
                        <StyledTextField
                          select
                          fullWidth
                          size="small"
                          label="Voice operation"
                          value={videoVoiceMode}
                          disabled={localizationBusy || localizationRunning}
                          data-testid={AD_CREATIVE_BATCH.drawerAiVideoVoiceMode}
                          onChange={(value) => {
                            setVideoVoiceMode(value as 'replace_with_tts' | 'dub_when_supported');
                            setVideoVoiceRightsSelection(null);
                          }}
                        >
                          <MenuItem value="replace_with_tts">Replace speech with approved localized audio</MenuItem>
                          <MenuItem value="dub_when_supported" disabled>Dub when supported (not available yet)</MenuItem>
                        </StyledTextField>
                        <VideoVoiceRightsPanel
                          projectId={projectId}
                          item={editableItem}
                          slotKey={videoSlotKey}
                          targetLocale={selectedLocale}
                          client={client}
                          selectedEvidenceId={videoVoiceRightsSelection?.evidenceId}
                          disabled={contentMutating}
                          onSelect={(selection) => {
                            setVideoVoiceRightsSelection(selection);
                            setSelectedApprovedAudioArtifactId('');
                          }}
                        />
                        <ApprovedAudioCandidatePanel
                          projectId={projectId}
                          itemId={editableItem.id}
                          slotKey={videoSlotKey}
                          targetLocale={selectedLocale}
                          captionArtifactId={exactTargetCaptionArtifactId}
                          voiceRightsEvidenceId={videoVoiceRightsSelection?.evidenceId}
                          approvedVoiceId={videoVoiceRightsSelection?.voiceId}
                          client={client}
                          selectedArtifactId={selectedApprovedAudioArtifactId || undefined}
                          disabled={contentMutating}
                          onSelectArtifact={(artifactId) => setSelectedApprovedAudioArtifactId(artifactId ?? '')}
                        />
                      </>
                    )}
                    <FormControlLabel
                      control={(
                        <Checkbox
                          size="small"
                          checked={videoOnScreenTextEnabled}
                          disabled={localizationBusy || localizationRunning || !videoSlotKey}
                          data-testid={AD_CREATIVE_BATCH.drawerAiVideoOnScreenTextToggle}
                          onChange={(event) => {
                            setVideoOnScreenTextEnabled(event.target.checked);
                            if (!event.target.checked) setVideoTextInspectionSelection(null);
                          }}
                        />
                      )}
                      label="Also replace text visible inside the video"
                      sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { typography: 'body2', pt: 0.35 } }}
                    />
                    {videoOnScreenTextEnabled && videoSlotKey && (
                      <VideoTextInspectionPanel
                        projectId={projectId}
                        item={editableItem}
                        slotKey={videoSlotKey}
                        targetLocale={selectedLocale}
                        client={client}
                        disabled={contentMutating}
                        onSelectionChange={setVideoTextInspectionSelection}
                      />
                    )}
                    <FormControlLabel
                      control={(
                        <Checkbox
                          size="small"
                          checked={videoCulturalVisualEnabled}
                          disabled={localizationBusy || localizationRunning || !videoSlotKey}
                          data-testid={AD_CREATIVE_BATCH.drawerAiVideoCulturalVisualToggle}
                          onChange={(event) => {
                            setVideoCulturalVisualEnabled(event.target.checked);
                            if (!event.target.checked) setCulturalVisualSelection(null);
                          }}
                        />
                      )}
                      label="Also adapt selected scenes for the target culture"
                      sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { typography: 'body2', pt: 0.35 } }}
                    />
                    {videoCulturalVisualEnabled && videoSlotKey && (
                      <CulturalVisualReviewPanel
                        projectId={projectId}
                        item={editableItem}
                        slotKey={videoSlotKey}
                        targetLocale={selectedLocale}
                        client={client}
                        disabled={contentMutating}
                        onSelectionChange={setCulturalVisualSelection}
                      />
                    )}
                  </Stack>
                )}

                {localizationClientBlocker && !activeLocalizationPreflight && (
                  <Alert severity="info">{localizationClientBlocker}</Alert>
                )}

                {activeLocalizationPreflight && (
                  <Box data-testid={AD_CREATIVE_BATCH.drawerAiCapability} aria-live="polite">
                    <Stack spacing={0.75}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          color={localizationSupported ? 'success' : 'default'}
                          variant="outlined"
                          label={localizationSupported ? 'Supported' : 'Unavailable'}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          data-testid={AD_CREATIVE_BATCH.drawerAiEstimate}
                          sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                        >
                          {localizationPreflightEstimate(activeLocalizationPreflight)}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {localizationCapabilitySummary(activeLocalizationPreflight)}
                      </Typography>
                      {activeLocalizationPreflight.component === 'video'
                        && activeLocalizationPreflight.culturalBilling && (
                        <Box
                          sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1,
                            bgcolor: 'action.hover',
                          }}
                        >
                          <Stack spacing={0.35}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              Cultural adaptation cost breakdown
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Scene generation: {activeLocalizationPreflight.culturalBilling.generation.totalRequestedSeconds.toLocaleString()} seconds with {activeLocalizationPreflight.culturalBilling.generation.model} · {formatMinorCurrency(
                                activeLocalizationPreflight.culturalBilling.generation.estimatedAmountMinor,
                                activeLocalizationPreflight.culturalBilling.generation.currency,
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Independent verification: {(activeLocalizationPreflight.culturalBilling.verifier.estimatedInputTokens
                                + activeLocalizationPreflight.culturalBilling.verifier.estimatedOutputTokens).toLocaleString()} tokens with {activeLocalizationPreflight.culturalBilling.verifier.model} · {formatMinorCurrency(
                                activeLocalizationPreflight.culturalBilling.verifier.estimatedAmountMinor,
                                activeLocalizationPreflight.culturalBilling.verifier.currency,
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Each selected scene is reserved and settled separately. Verification never inherits the video model’s usage.
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                      {localizationUnsupportedReasons.map((reason, index) => (
                        <Alert
                          key={`${reason.code}-${index}`}
                          severity="warning"
                          data-testid={AD_CREATIVE_BATCH.drawerAiUnsupportedReason(index)}
                        >
                          {reason.message}
                        </Alert>
                      ))}
                    </Stack>
                  </Box>
                )}

                {localizationJob && (
                  <Box data-testid={AD_CREATIVE_BATCH.drawerAiProgress} aria-live="polite">
                    <Stack spacing={0.75}>
                      {!isLocalizationSnapshotTerminal(localizationJob) && <LinearProgress />}
                      <Typography variant="caption" data-testid={AD_CREATIVE_BATCH.drawerAiStatus}>
                        {localizationStatusCopy(localizationJob)}
                      </Typography>
                      {localizationJobError(localizationJob) && (
                        <Alert severity="error">{localizationJobError(localizationJob)}</Alert>
                      )}
                      {isLocalizationSnapshotSuccessful(localizationJob) && (
                        <Alert severity="info" data-testid={AD_CREATIVE_BATCH.drawerAiReviewRequired}>
                          AI output was saved as review required. Review the localized component together with copy, media, and destination before approval. Nothing was published to Meta.
                        </Alert>
                      )}
                      {hasCompletedCaptionTier(localizationJob)
                        && !requiresAiVideoReview
                        && Boolean(videoReviewSlotBinding) && (
                        <Stack spacing={0.75}>
                          {!captionReview && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={captionReviewLoading}
                              data-testid={AD_CREATIVE_BATCH.drawerCaptionReviewLoad}
                              onClick={() => void loadCaptionReview()}
                              startIcon={captionReviewLoading ? <CircularProgress size={14} /> : undefined}
                            >
                              Review caption cues
                            </Button>
                          )}
                          {captionReviewError && (
                            <Alert severity="error" data-testid={AD_CREATIVE_BATCH.drawerCaptionReviewError}>
                              {captionReviewError}
                            </Alert>
                          )}
                          {captionReview && (
                            <Box
                              data-testid={AD_CREATIVE_BATCH.drawerCaptionReviewPanel}
                              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
                            >
                              <Stack spacing={1}>
                                <Alert severity="info">
                                  This is the separately rendered, burned-in Segmently derivative. The immutable WebVTT and cue list remain available for audit; the source video and every Meta/Facebook asset remain unchanged.
                                </Alert>
                                {captionPlaybackSource ? (
                                  <Box
                                    component="video"
                                    src={captionPlaybackSource.videoUrl}
                                    poster={captionPlaybackSource.posterUrl}
                                    controls
                                    preload="metadata"
                                    aria-label={`Burned-in caption derivative · ${captionReview.targetLocale}`}
                                    lang={captionReview.targetLocale}
                                    dir={localeDirection(captionReview.targetLocale)}
                                    sx={{ width: '100%', maxHeight: 240, objectFit: 'contain', bgcolor: 'grey.100', borderRadius: 1 }}
                                  />
                                ) : (
                                  <Alert severity="warning">
                                    Localized derivative playback is unavailable because its output evidence does not exactly match the current Segmently copy, source asset, revision, locale, and SHA-256. The immutable WebVTT and cue list remain available below.
                                  </Alert>
                                )}
                                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <Chip size="small" label="Review required" color="warning" variant="outlined" />
                                  {captionPlaybackSource && (
                                    <Chip size="small" label="Burned-in derivative" color="success" variant="outlined" />
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    {captionReview.sourceLocale} → {captionReview.targetLocale}
                                  </Typography>
                                  {captionReview.artifact && (
                                    <Button
                                      size="small"
                                      component="a"
                                      href={captionReview.artifact.vttUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      data-testid={AD_CREATIVE_BATCH.drawerCaptionReviewSidecarLink}
                                    >
                                      Open WebVTT
                                    </Button>
                                  )}
                                </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                  {captionReview.transcriptEvidence
                                    ? `Timing evidence: ${captionReview.transcriptEvidence.verifierEvidenceId} · `
                                    : ''}
                                  Exact-output evidence: {captionReview.verificationEvidence.evidenceId}
                                  {' · '}
                                  {captionPlaybackSource
                                    ? `${captionReview.verificationEvidence.checks.length}/${captionReview.verificationEvidence.checks.length} verification checks passed · language, burn-in, and pixel safe area verified`
                                    : 'evidence does not match the current creative state; playback blocked'}
                                </Typography>
                                <Box component="section" aria-label="Caption cue fallback">
                                  <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                                    Caption cue fallback
                                  </Typography>
                                  <Stack spacing={0.75}>
                                    {(captionReview.cues ?? []).map((cue, index) => (
                                      <Box
                                        key={cue.id}
                                        data-testid={AD_CREATIVE_BATCH.drawerCaptionReviewCue(index)}
                                        sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1 }}
                                      >
                                        <Stack spacing={0.5}>
                                          <Typography variant="caption" color="text.secondary">
                                            {formatCaptionCueTime(cue.startMs)} – {formatCaptionCueTime(cue.endMs)}
                                            {cue.speaker ? ` · ${cue.speaker}` : ''}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            lang={captionReview.sourceLocale}
                                            dir={localeDirection(captionReview.sourceLocale)}
                                            sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                                          >
                                            {cue.sourceText}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            lang={captionReview.targetLocale}
                                            dir={localeDirection(captionReview.targetLocale)}
                                            sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                                          >
                                            {cue.translatedText}
                                          </Typography>
                                        </Stack>
                                      </Box>
                                    ))}
                                  </Stack>
                                </Box>
                                {captionReview.artifact && (
                                  <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                    SHA-256: {captionReview.artifact.vttSha256}
                                  </Typography>
                                )}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      )}
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {!isLocalizationSnapshotTerminal(localizationJob) && (
                          <Button
                            size="small"
                            color="inherit"
                            variant="outlined"
                            disabled={localizationActionBusy}
                            data-testid={AD_CREATIVE_BATCH.drawerAiCancel}
                            onClick={() => void mutateLocalizationJob('cancel')}
                          >
                            Cancel localization
                          </Button>
                        )}
                        {isLocalizationSnapshotResumable(localizationJob) && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={localizationActionBusy}
                            data-testid={AD_CREATIVE_BATCH.drawerAiResume}
                            onClick={() => void mutateLocalizationJob('resume')}
                          >
                            Resume localization
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                )}

                {localizationError && <Alert severity="error">{localizationError}</Alert>}

                {!localizationJob && (
                  <Stack spacing={0.75}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={localizationBusy || Boolean(localizationClientBlocker)}
                      data-testid={AD_CREATIVE_BATCH.drawerAiPreflight}
                      onClick={() => void runLocalizationPreflight()}
                      startIcon={localizationBusy ? <CircularProgress size={14} /> : undefined}
                    >
                      {activeLocalizationPreflight ? 'Refresh capability & estimate' : 'Check capability & estimate'}
                    </Button>
                    {localizationSupported && (
                      <>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              size="small"
                              checked={localizationConfirmed}
                              data-testid={AD_CREATIVE_BATCH.drawerAiConfirmation}
                              onChange={(event) => setLocalizationConfirmed(event.target.checked)}
                            />
                          )}
                          label="I confirm this metered AI run and will review the result."
                          sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { typography: 'caption', pt: 0.5 } }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!localizationConfirmed || localizationBusy}
                          data-testid={AD_CREATIVE_BATCH.drawerAiStart}
                          onClick={() => void startLocalizationJob()}
                          startIcon={localizationBusy ? <CircularProgress size={14} /> : undefined}
                        >
                          Start {localizationComponent === 'images' ? 'image' : localizationComponent} localization
                        </Button>
                      </>
                    )}
                  </Stack>
                )}
              </Stack>
            </Box>
          )}

          {!projection.isSource && (
            <Box
              data-testid={AD_CREATIVE_BATCH.drawerComponentReview}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25, minWidth: 0 }}
            >
              <Stack spacing={1}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Component review
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Accept, return, or reject each target component. Final locale approval stays separate and never publishes or changes Meta.
                  </Typography>
                </Box>
                {localizationIndexLoading && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">Loading exact review state…</Typography>
                  </Stack>
                )}
                {localizationIndexError && (
                  <Alert severity="warning">{localizationIndexError}</Alert>
                )}
                {!localizationIndexLoading && !localizationIndexError && !selectedLocalizationVariant && (
                  <Typography variant="caption" color="text.secondary">
                    Save or generate target-language content before reviewing components.
                  </Typography>
                )}
                {selectedLocalizationVariant && (
                  <>
                    {requiresAiVideoReview && (
                      <CaptionVideoEvidenceReviewPanel
                        review={captionReview}
                        playbackSource={captionPlaybackSource}
                        loading={captionReviewLoading}
                        error={captionReviewError}
                        canLoad={Boolean(videoReviewSlotBinding)}
                        gate={videoReviewGate}
                        onLoad={() => void loadCaptionReview()}
                        onLoadedMetadata={verifyLocalizedVideoPlayback}
                        onPlaybackInvalid={rejectLocalizedVideoPlayback}
                      />
                    )}
                    {requiresAiImageReview && editableItem && (
                      <ImageLocalizationReviewPanel
                        key={`${editableItem.id}:${selectedLocale}:${imageReviewBindingScope}`}
                        client={client}
                        projectId={projectId}
                        item={editableItem}
                        targetLocale={selectedLocale}
                        currentSourceComponentHash={selectedLocalizationVariant.sourceComponentHashes?.images}
                        componentStatus={selectedLocalizationVariant.componentStatus.images}
                        binding={imageReviewBinding}
                        onGateChange={setImageReviewGate}
                      />
                    )}
                    <StyledTextField
                      label="Review note (optional)"
                      size="small"
                      fullWidth
                      value={componentReviewNotes}
                      disabled={localeMutating}
                      data-testid={AD_CREATIVE_BATCH.drawerComponentReviewNotes}
                      onChange={(value) => setComponentReviewNotes(String(value))}
                      helperText="The same note is attached only to the next component decision."
                    />
                    {reviewableComponents.map((component) => {
                      const status = selectedLocalizationVariant.componentStatus[component];
                      const stale = status === 'stale';
                      const imageEvidenceBlocked = component === 'images'
                        && status !== 'ready'
                        && requiresAiImageReview
                        && !imageReviewGate.canAccept;
                      const videoEvidenceBlocked = component === 'video'
                        && status !== 'ready'
                        && requiresAiVideoReview
                        && !videoReviewGate.canAccept;
                      return (
                        <Box
                          key={component}
                          data-testid={AD_CREATIVE_BATCH.drawerComponentReviewRow(component)}
                          sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}
                        >
                          <Stack spacing={0.75}>
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                                {componentLabel(component)}
                              </Typography>
                              <Chip
                                size="small"
                                color={localeStatusColor(status)}
                                label={status.replace(/_/g, ' ')}
                              />
                            </Stack>
                            {stale && (
                              <Alert severity="warning">
                                The source changed. Accepting the existing target explicitly reconciles only this component with the current source hash.
                              </Alert>
                            )}
                            {imageEvidenceBlocked && (
                              <Typography variant="caption" color="warning.main" sx={{ overflowWrap: 'anywhere' }}>
                                Accept is locked until immutable evidence matches every current AI-generated image and OCR, brand, claims, and safe-zone checks pass.
                              </Typography>
                            )}
                            {videoEvidenceBlocked && (
                              <Typography
                                variant="caption"
                                color="warning.main"
                                data-testid={AD_CREATIVE_BATCH.drawerVideoReviewGate}
                                sx={{ overflowWrap: 'anywhere' }}
                              >
                                {videoReviewGate.reason}
                              </Typography>
                            )}
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={localeMutating
                                  || status === 'ready'
                                  || imageEvidenceBlocked
                                  || videoEvidenceBlocked}
                                data-testid={AD_CREATIVE_BATCH.drawerComponentAccept(component)}
                                onClick={() => void reviewLocaleComponent(component, 'approved', stale)}
                              >
                                {stale ? 'Accept existing' : 'Accept'}
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                disabled={localeMutating}
                                data-testid={AD_CREATIVE_BATCH.drawerComponentRequestChanges(component)}
                                onClick={() => void reviewLocaleComponent(component, 'changes_requested')}
                              >
                                Request changes
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                variant="text"
                                disabled={localeMutating}
                                data-testid={AD_CREATIVE_BATCH.drawerComponentReject(component)}
                                onClick={() => void reviewLocaleComponent(component, 'rejected')}
                              >
                                Reject
                              </Button>
                              <Button
                                size="small"
                                color="inherit"
                                variant="text"
                                disabled={localeMutating}
                                data-testid={AD_CREATIVE_BATCH.drawerComponentRegenerate(component)}
                                onClick={() => prepareComponentRegeneration(component)}
                              >
                                {component === 'destination' ? 'Edit' : 'Regenerate'}
                              </Button>
                            </Stack>
                          </Stack>
                        </Box>
                      );
                    })}
                  </>
                )}
              </Stack>
            </Box>
          )}

          {previewImage ? (
            projection.previewMediaKind === 'video' ? (
              <Box
                component="video"
                data-testid={AD_CREATIVE_BATCH.drawerPreview}
                src={previewImage}
                controls
                muted
                preload="metadata"
                sx={{ width: '100%', maxHeight: 320, objectFit: 'contain', bgcolor: 'grey.100', borderRadius: 1 }}
              />
            ) : (
              <Box
                component="img"
                data-testid={AD_CREATIVE_BATCH.drawerPreview}
                src={previewImage}
                alt={localizedTitle}
                loading="lazy"
                decoding="async"
                onClick={() => setLightboxOpen(true)}
                sx={{ width: '100%', maxHeight: 320, objectFit: 'contain', bgcolor: 'grey.100', borderRadius: 1, cursor: 'zoom-in' }}
              />
            )
          ) : (
            <Box
              data-testid={AD_CREATIVE_BATCH.drawerPreview}
              sx={{ width: '100%', minHeight: 160, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Typography variant="body2" color="text.secondary">No preview yet</Typography>
            </Box>
          )}

          <Box
            data-testid={AD_CREATIVE_BATCH.drawerMediaSection}
            sx={{ borderTop: 1, borderColor: 'divider', pt: 1.25 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Localized media</Typography>
            {sourceMediaSlots.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                The source manifest has no media slots available for localization.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {sourceMediaSlots.map(([slotKey, slot]) => {
                  const exactEntry = getExactMediaEntry(slot.translations, selectedLocale);
                  const sourceEntry = getExactMediaEntry(slot.translations, sourceProjection.sourceLocale);
                  const usesFallback = !exactEntry && Boolean(showFallback && sourceEntry);
                  const displayedEntry = exactEntry ?? (usesFallback ? sourceEntry : undefined);
                  const mediaUrl = displayedEntry?.asset?.original ?? displayedEntry?.asset?.small;
                  const running = mediaMutatingSlot === slotKey;
                  const canUpload = slot.mediaKind === 'image'
                    || slot.mediaKind === 'poster'
                    || slot.mediaKind === 'video';
                  const uploadEvidence = manualMediaEvidence[`${selectedLocale}:${slotKey}`];
                  const stateLabel = exactEntry?.disabled
                    ? 'Explicitly disabled'
                    : exactEntry?.asset
                      ? 'Exact locale asset'
                      : usesFallback
                        ? 'Source fallback preview'
                        : 'Missing locale asset';
                  const stateColor = exactEntry?.disabled
                    ? 'warning'
                    : exactEntry?.asset
                      ? 'success'
                      : usesFallback
                        ? 'info'
                        : 'default';
                  return (
                    <Box
                      key={slotKey}
                      data-testid={AD_CREATIVE_BATCH.drawerMediaSlot(slotKey)}
                      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
                    >
                      <Stack spacing={0.75}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{slotKey}</Typography>
                          <Chip size="small" variant="outlined" label={slot.mediaKind} />
                          <Chip
                            size="small"
                            color={stateColor}
                            label={stateLabel}
                            data-testid={AD_CREATIVE_BATCH.drawerMediaState(slotKey)}
                          />
                        </Stack>
                        {mediaUrl && !displayedEntry?.disabled && (
                          slot.mediaKind === 'video' ? (
                            <Box
                              component="video"
                              src={mediaUrl}
                              controls
                              muted
                              preload="metadata"
                              data-testid={AD_CREATIVE_BATCH.drawerMediaPreview(slotKey)}
                              sx={{ width: '100%', maxHeight: 190, objectFit: 'contain', bgcolor: 'grey.100', borderRadius: 1 }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={mediaUrl}
                              alt={`${slotKey} · ${selectedLocale}`}
                              loading="lazy"
                              decoding="async"
                              data-testid={AD_CREATIVE_BATCH.drawerMediaPreview(slotKey)}
                              sx={{ width: '100%', maxHeight: 190, objectFit: 'contain', bgcolor: 'grey.100', borderRadius: 1 }}
                            />
                          )
                        )}
                        {exactEntry?.disabled && (
                          <Typography variant="caption" color="warning.main">
                            This slot is intentionally disabled for {selectedLocale}. It is not missing and will not fall back.
                          </Typography>
                        )}
                        {!exactEntry && (
                          <Typography variant="caption" color="text.secondary">
                            {usesFallback
                              ? `Previewing ${sourceProjection.sourceLocale}; publish still requires an exact ${selectedLocale} decision.`
                              : `No exact ${selectedLocale} media decision exists for this slot.`}
                          </Typography>
                        )}
                        {slot.mediaKind === 'video' && !uploadEvidence && (
                          <Typography variant="caption" color="text.secondary">
                            Upload an MP4/WebM up to 128 MB. Files over 30 MB upload directly to a short-lived Segmently staging target, then the server verifies the exact generation, bytes, decoded container, duration, and dimensions before it can be applied.
                          </Typography>
                        )}
                        {running && manualVideoUploadProgress?.slotKey === slotKey && (
                          <Box
                            data-testid={AD_CREATIVE_BATCH.drawerMediaProgress(slotKey)}
                            sx={{ bgcolor: 'info.50', borderRadius: 1, px: 1, py: 0.75 }}
                          >
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                              {manualVideoUploadProgress.phase === 'preparing'
                                ? 'Preparing secure direct upload…'
                                : manualVideoUploadProgress.phase === 'uploading'
                                  ? `Uploading directly to Segmently… ${manualVideoUploadProgress.percent}%`
                                  : 'Finalizing: verifying exact object, hash, and decoded video…'}
                            </Typography>
                            <LinearProgress
                              variant={manualVideoUploadProgress.phase === 'finalizing' ? 'indeterminate' : 'determinate'}
                              value={manualVideoUploadProgress.percent}
                            />
                          </Box>
                        )}
                        {uploadEvidence && (
                          <Box
                            data-testid={AD_CREATIVE_BATCH.drawerMediaEvidence(slotKey)}
                            sx={{ bgcolor: 'success.50', borderRadius: 1, px: 1, py: 0.75 }}
                          >
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                              Server-verified {uploadEvidence.asset.mimeType} · {uploadEvidence.asset.width}×{uploadEvidence.asset.height}
                              {uploadEvidence.asset.durationMs
                                ? ` · ${formatMediaDuration(uploadEvidence.asset.durationMs)}`
                                : ''}
                              {' · '}{formatMediaBytes(uploadEvidence.byteSize)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                              Evidence {uploadEvidence.evidenceId}
                              {uploadEvidence.objectGeneration ? ` · GCS generation ${uploadEvidence.objectGeneration}` : ''}
                              {uploadEvidence.asset.sha256 ? ` · SHA-256 ${uploadEvidence.asset.sha256}` : ''}
                            </Typography>
                          </Box>
                        )}
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={contentMutating || projection.isSource}
                            data-testid={AD_CREATIVE_BATCH.drawerMediaReuse(slotKey)}
                            onClick={() => void mutateMedia(slotKey, { action: 'reuse_source' }, `Source media reused for ${slotKey}.`)}
                          >
                            Reuse source
                          </Button>
                          {projection.isSource && sourceEntry?.asset && editableItem.languages.length > 1 && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={contentMutating}
                              data-testid={AD_CREATIVE_BATCH.drawerMediaReuseAll(slotKey)}
                              onClick={() => void reuseSourceMediaForAllLocales(slotKey)}
                            >
                              Use this asset for all languages
                            </Button>
                          )}
                          {canUpload && (
                            <Button
                              component="label"
                              size="small"
                              variant="outlined"
                              disabled={contentMutating}
                              data-testid={AD_CREATIVE_BATCH.drawerMediaUpload(slotKey)}
                            >
                              {exactEntry?.asset ? 'Replace' : 'Upload'} {slot.mediaKind === 'video' ? 'video' : 'image'}
                              <input
                                hidden
                                type="file"
                                accept={slot.mediaKind === 'video'
                                  ? 'video/mp4,video/webm'
                                  : 'image/jpeg,image/png,image/webp'}
                                data-testid={AD_CREATIVE_BATCH.drawerMediaFileInput(slotKey)}
                                onChange={(event) => {
                                  const file = event.currentTarget.files?.[0];
                                  event.currentTarget.value = '';
                                  if (file) void uploadMedia(slotKey, slot.mediaKind, file);
                                }}
                              />
                            </Button>
                          )}
                          <Button
                            size="small"
                            color="warning"
                            variant="outlined"
                            disabled={contentMutating || Boolean(exactEntry?.disabled)}
                            data-testid={AD_CREATIVE_BATCH.drawerMediaDisable(slotKey)}
                            onClick={() => void mutateMedia(
                              slotKey,
                              { action: 'disable', reason: `Explicitly disabled for ${selectedLocale} in the Segmently creative editor.` },
                              `${slotKey} explicitly disabled.`,
                            )}
                          >
                            Disable
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>

          {videoSlotKey && hasExactSourceVideoAsset(editableItem, videoSlotKey) && (
            <VideoTranscriptPanel
              projectId={projectId}
              item={editableItem}
              slotKey={videoSlotKey}
              client={client}
              automaticTranscriptionModel={VIDEO_TRANSCRIPTION_MODEL}
              disabled={contentMutating}
              onCurrentTranscriptChange={() => {
                setLocalizationPreflight(null);
                setLocalizationJob(null);
                setLocalizationConfirmed(false);
                setLocalizationError(null);
                setCaptionReview(null);
                setCaptionReviewError(null);
              }}
            />
          )}

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
            {(editableItem.validationState?.checks ?? []).map((check) => (
              <Tooltip key={check.id} title={check.message} arrow>
                <Chip
                  size="small"
                  tabIndex={0}
                  variant="outlined"
                  color={check.status === 'fail' ? (check.severity === 'error' ? 'error' : 'warning') : 'success'}
                  data-testid={AD_CREATIVE_BATCH.drawerValidationChip(check.id)}
                  label={`${check.status === 'fail' ? '✕' : '✓'} ${check.id}`}
                  aria-label={`${check.id}: ${check.message}`}
                />
              </Tooltip>
            ))}
            <Button
              size="small"
              data-testid={AD_CREATIVE_BATCH.drawerValidate}
              onClick={() => void validate()}
              disabled={validating}
              startIcon={validating ? <CircularProgress size={12} /> : undefined}
            >
              {editableItem.validationState ? 'Re-validate' : 'Validate'}
            </Button>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
          {saveStatus && <Alert severity="success" onClose={() => setSaveStatus(null)}>{saveStatus}</Alert>}

          {!projection.isSource && (editableItem.localizedCopy.cardCopy?.length ?? 0) > 0 && (
            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.25 }}>
              <CarouselLocalizationEditor
                projectId={projectId}
                item={editableItem}
                selectedLocale={selectedLocale}
                sourceLocale={sourceLocale}
                client={client}
                disabled={contentMutating}
                onRefresh={() => onRefreshItem(editableItem.id)}
              />
            </Box>
          )}

          <Stack spacing={1.25}>
            {visibleCopyFields.map(({ key, label, multiline }) => {
              const claimKey = key as CopyFieldKey;
              const claimState = editableItem.claimSafety && CLAIM_FIELDS.includes(claimKey)
                ? claimStateForField(claimKey, editableItem.dataUsage ?? [], (editableItem.claimSafety.blockedCount ?? 0) > 0)
                : null;
              const maxChars = copyFieldConstraints[key]?.maxChars;
              const graphemeCount = countUnicodeGraphemes(String(copyDraft[key] ?? ''));
              const overLimit = Boolean(maxChars && graphemeCount > maxChars);
              return (
                <Stack key={key} spacing={0.5}>
                  {compareWithSource && !projection.isSource && (
                    <Box
                      data-testid={AD_CREATIVE_BATCH.drawerSourceField(key)}
                      lang={sourceLocale}
                      dir={sourceLocaleDirection}
                      data-source-direction={sourceLocaleDirection}
                      sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1, py: 0.75 }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {`${label} · ${sourceProjection?.sourceLocale ?? editableItem.defaultLanguage}`}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {sourceProjection?.copy[key].value === undefined
                          ? 'Not set in source'
                          : sourceProjection.copy[key].value}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ position: 'relative' }}>
                  <StyledTextField
                    label={`${label} · ${selectedLocale}`}
                    size="small"
                    fullWidth
                    multiline={multiline}
                    minRows={multiline ? 2 : undefined}
                    value={String(copyDraft[key] ?? '')}
                    error={overLimit}
                    helperText={maxChars
                      ? `${graphemeCount}/${maxChars} visible characters${overLimit ? ' · shorten before saving' : ''}`
                      : undefined}
                    data-testid={AD_CREATIVE_BATCH.drawerCopyField(key)}
                    onFocus={() => { void getFreshLocalizationManifest().catch(() => undefined); }}
                    onChange={(value) => {
                      setCopyDraft((current) => ({ ...current, [key]: String(value) }));
                      setTouchedFields((current) => new Set(current).add(key));
                    }}
                  />
                  {claimState && (
                    <Chip
                      size="small"
                      data-testid={AD_CREATIVE_INSIGHT.claimChip(key)}
                      label={claimState}
                      color={claimState === 'sourced' ? 'success' : claimState === 'blocked' ? 'error' : 'default'}
                      sx={{ position: 'absolute', top: -8, right: 8, height: 16, fontSize: 10 }}
                    />
                  )}
                  </Box>
                  {!projection.copy[key].hasRequestedValue && (
                    <Typography variant="caption" color="text.secondary">
                      No authored {selectedLocale} value yet. Saving an empty field creates an explicit blank override.
                    </Typography>
                  )}
                </Stack>
              );
            })}
            {editableItem.claimSafety && (
              <Typography variant="caption" color="text.secondary" data-testid={AD_CREATIVE_INSIGHT.claimSummary}>
                {`Claim safety: ${editableItem.claimSafety.sourcedCount ?? 0} sourced · ${editableItem.claimSafety.inferredCount ?? 0} inferred · ${editableItem.claimSafety.blockedCount ?? 0} blocked`}
                {(editableItem.claimSafety.unsupportedClaims?.length ?? 0) > 0
                  ? ` · ${editableItem.claimSafety.unsupportedClaims!.length} unsupported dropped`
                  : ''}
              </Typography>
            )}
            {copyChanged && (
              <Button
                variant="contained"
                size="small"
                data-testid={AD_CREATIVE_BATCH.drawerSaveCopy}
                onClick={() => void saveCopy()}
                disabled={saving || localeMutating || copyLimitViolation}
                startIcon={saving ? <CircularProgress size={14} /> : undefined}
              >
                Validate & save {selectedLocale}
              </Button>
            )}
            {!projection.isSource && ['review_required', 'draft', 'partial'].includes(projection.status) && (
              <Stack spacing={0.5}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={contentMutating || copyChanged || destinationTouched || !localeComponentsAccepted}
                  data-testid={AD_CREATIVE_BATCH.drawerApproveLocale}
                  onClick={() => void approveLocale()}
                >
                  Approve {selectedLocale}
                </Button>
                {!localeComponentsAccepted && (
                  <Typography variant="caption" color="text.secondary">
                    Accept every applicable component before final locale approval.
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>

          <Box
            data-testid={AD_CREATIVE_BATCH.drawerDestinationSection}
            sx={{ borderTop: 1, borderColor: 'divider', pt: 1.25 }}
          >
            <Stack spacing={1}>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>Destination</Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color={exactDestination ? 'success' : destinationUsesFallback ? 'info' : 'default'}
                  label={exactDestination?.type === 'none'
                    ? 'Explicit none'
                    : exactDestination?.type === 'url'
                      ? 'Exact URL'
                      : exactDestination?.type === 'web_placement'
                        ? 'Exact funnel'
                        : exactDestination?.type === 'article'
                          ? 'Exact article'
                      : destinationUsesFallback
                        ? 'Source fallback preview'
                        : 'Missing'}
                  data-testid={AD_CREATIVE_BATCH.drawerDestinationState}
                />
              </Stack>
              {destinationUsesFallback && previewDestination?.type === 'url' && (
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {`Preview only · ${sourceProjection.sourceLocale}: ${previewDestination.url}`}
                </Typography>
              )}
              {destinationUsesFallback && previewDestination?.type === 'web_placement' && (
                <Typography variant="caption" color="text.secondary">
                  {`Preview only · ${sourceProjection.sourceLocale}: funnel ${previewDestination.webPlacementAlias ?? 'unknown'}`}
                </Typography>
              )}
              {destinationUsesFallback && previewDestination?.type === 'article' && (
                <Typography variant="caption" color="text.secondary">
                  {`Preview only · ${sourceProjection.sourceLocale}: article ${previewDestination.label ?? previewDestination.articleId ?? 'unknown'}`}
                </Typography>
              )}
              {!exactDestination && !destinationUsesFallback && (
                <Typography variant="caption" color="text.secondary">
                  No exact destination decision exists for {selectedLocale}. Missing is different from explicit none.
                </Typography>
              )}
              {exactDestination?.type === 'none' && (
                <Typography variant="caption" color="text.secondary">
                  This locale intentionally has no destination. It will never inherit the source URL implicitly.
                </Typography>
              )}
              {sourceDestination === undefined ? (
                <Alert severity="warning">
                  The source manifest has no destination entry, so a locale destination cannot be saved until the source is defined.
                </Alert>
              ) : (
                <>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={destinationMode}
                    onChange={(_event, nextMode: DrawerDestinationMode | null) => {
                      if (!nextMode) return;
                      setDestinationMode(nextMode);
                      setDestinationReference('');
                      setDestinationFallbackApproved(false);
                      setDestinationTouched(true);
                    }}
                    disabled={contentMutating}
                  >
                    <ToggleButton value="funnel" data-testid={AD_CREATIVE_BATCH.drawerDestinationModeFunnel}>Funnel</ToggleButton>
                    <ToggleButton value="article" data-testid={AD_CREATIVE_BATCH.drawerDestinationModeArticle}>Article</ToggleButton>
                    <ToggleButton value="url" data-testid={AD_CREATIVE_BATCH.drawerDestinationModeUrl}>URL</ToggleButton>
                    <ToggleButton value="none" data-testid={AD_CREATIVE_BATCH.drawerDestinationModeNone}>Explicit none</ToggleButton>
                  </ToggleButtonGroup>
                  {(destinationMode === 'funnel' || destinationMode === 'article') && (
                    <StyledTextField
                      select
                      fullWidth
                      size="small"
                      label={destinationMode === 'funnel'
                        ? `Published funnel · ${selectedLocale}`
                        : `Published article · ${selectedLocale}`}
                      value={destinationReference}
                      disabled={contentMutating || destinationCatalogLoading}
                      data-testid={AD_CREATIVE_BATCH.drawerDestinationReference}
                      onChange={(value) => {
                        setDestinationReference(String(value));
                        setDestinationFallbackApproved(false);
                        setDestinationTouched(true);
                      }}
                      helperText={destinationCatalogLoading
                        ? 'Loading project destinations…'
                        : destinationOptions.length === 0
                          ? `No published ${destinationMode}s were found in this project.`
                          : 'Exact-locale matches appear first. Mismatches require explicit fallback approval.'}
                    >
                      {destinationReference && !destinationOptions.some((option) => option.id === destinationReference) && (
                        <MenuItem value={destinationReference}>
                          {`Current selection · ${destinationReference} · unavailable or locale-incompatible`}
                        </MenuItem>
                      )}
                      {destinationOptions.map((option) => (
                        <MenuItem
                          key={`${option.kind}:${option.id}`}
                          value={option.id}
                          data-testid={AD_CREATIVE_BATCH.drawerDestinationOption(option.kind, option.id)}
                        >
                          {`${option.label} · ${option.compatible ? `supports ${selectedLocale}` : option.languages.join(', ') || 'locale metadata unavailable'}`}
                        </MenuItem>
                      ))}
                    </StyledTextField>
                  )}
                  {destinationMode === 'url' && (
                    <StyledTextField
                      label={`Destination URL · ${selectedLocale}`}
                      size="small"
                      fullWidth
                      value={destinationUrl}
                      disabled={contentMutating}
                      data-testid={AD_CREATIVE_BATCH.drawerDestinationUrl}
                      onChange={(value) => {
                        setDestinationUrl(String(value));
                        setDestinationTouched(true);
                      }}
                      helperText={destinationUrl && !isHttpUrl(destinationUrl)
                        ? 'Use a full http:// or https:// URL.'
                        : undefined}
                    />
                  )}
                  {destinationCatalogError && (
                    <Alert severity="warning" data-testid={AD_CREATIVE_BATCH.drawerDestinationCatalogError}>
                      {destinationCatalogError}
                    </Alert>
                  )}
                  {selectedDestinationOption && (
                    <Alert
                      severity={selectedDestinationOption.compatible ? 'success' : 'warning'}
                      data-testid={AD_CREATIVE_BATCH.drawerDestinationEvidence}
                    >
                      {selectedDestinationOption.reason}
                    </Alert>
                  )}
                  {!destinationTouched && destinationCatalog?.selectedCompatibility && (
                    <Alert
                      severity={destinationCatalog.selectedCompatibility.status === 'blocked'
                        ? 'warning'
                        : destinationCatalog.selectedCompatibility.status === 'warning'
                          ? 'info'
                          : 'success'}
                      data-testid={AD_CREATIVE_BATCH.drawerDestinationEvidence}
                    >
                      {destinationCatalog.selectedCompatibility.message}
                    </Alert>
                  )}
                  {((!destinationTouched && destinationCatalog?.selectedCompatibility.approvalRequired)
                    || (destinationTouched && selectedDestinationOption && !selectedDestinationOption.compatible)) && (
                    <FormControlLabel
                      control={(
                        <Checkbox
                          size="small"
                          checked={destinationFallbackApproved}
                          data-testid={AD_CREATIVE_BATCH.drawerDestinationFallbackApproval}
                          onChange={(event) => setDestinationFallbackApproved(event.target.checked)}
                        />
                      )}
                      label={`I explicitly approve using this destination without exact ${selectedLocale} content. This approval applies only to validation/export, never to Meta source data.`}
                      sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { typography: 'caption', pt: 0.5 } }}
                    />
                  )}
                  {destinationCatalog && destinationCatalog.options.some((option) => option.compatible) && (
                    <Typography variant="caption" color="text.secondary" data-testid={AD_CREATIVE_BATCH.drawerDestinationSuggestions}>
                      {`Compatible suggestions: ${destinationCatalog.options
                        .filter((option) => option.compatible)
                        .slice(0, 5)
                        .map((option) => `${option.kind}: ${option.label}`)
                        .join(' · ')}`}
                    </Typography>
                  )}
                  {destinationTouched && (
                    <Button
                      variant="contained"
                      size="small"
                      disabled={contentMutating
                        || (destinationMode === 'url' && !isHttpUrl(destinationUrl))
                        || ((destinationMode === 'funnel' || destinationMode === 'article') && !selectedDestinationOption)}
                      data-testid={AD_CREATIVE_BATCH.drawerDestinationSave}
                      onClick={() => void saveDestination()}
                      startIcon={destinationSaving ? <CircularProgress size={14} /> : undefined}
                    >
                      Validate & save destination
                    </Button>
                  )}
                </>
              )}
            </Stack>
          </Box>

          {/* Data sources audit (mock 03): persisted dataUsage[], resolvable
              against the pinned analysis. Inferred slots get an honest row. */}
          {(editableItem.dataUsage?.length ?? 0) > 0 && (
            <Box data-testid={AD_CREATIVE_INSIGHT.dataSources} sx={{ borderTop: 1, borderColor: 'divider', pt: 1.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                Data sources
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                  {`· ${editableItem.dataUsage!.length} item${editableItem.dataUsage!.length === 1 ? '' : 's'}`}
                </Typography>
              </Typography>
              <Stack spacing={0.75}>
                {editableItem.dataUsage!.map((entry, index) => (
                  <Box
                    key={`${entry.sourcePath}-${entry.slot ?? index}`}
                    data-testid={AD_CREATIVE_INSIGHT.dataSourceRow(index)}
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      {entry.category && (
                        <Chip
                          size="small"
                          color={INSIGHT_CATEGORY_COLOR[entry.category] ?? 'info'}
                          label={entry.category}
                          sx={{ height: 18, fontSize: 10 }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                        {entry.sourcePath}
                      </Typography>
                      {entry.slot && (
                        <Chip size="small" variant="outlined" label={entry.slot} sx={{ height: 16, fontSize: 10 }} />
                      )}
                    </Stack>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>{entry.justification}</Typography>
                    {entry.value && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                        “{entry.value}”
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ImageIcon />}
              data-testid={AD_CREATIVE_BATCH.drawerRegenerateImage}
              disabled={actionRunning || !projection.isSource}
              onClick={() => void runAction(onRegenerateImage)}
            >
              Regenerate image
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<NotesIcon />}
              data-testid={AD_CREATIVE_BATCH.drawerVariateCopy}
              disabled={actionRunning || !projection.isSource}
              onClick={() => void runAction(onVariateCopy)}
            >
              Variate copy
            </Button>
            {!isVideoCreativeItem(editableItem) && onMakeVideo && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SmartDisplayIcon />}
                data-testid={AD_CREATIVE_BATCH.drawerMakeVideo}
                disabled={actionRunning || !projection.isSource}
                onClick={() => void runAction(onMakeVideo)}
              >
                Make video from this creative
              </Button>
            )}
          </Stack>

          {!projection.isSource && (
            <Typography variant="caption" color="text.secondary">
              Generation actions remain on the source locale in this manual-localization phase. Target-language AI adaptation is a separate, review-gated workflow.
            </Typography>
          )}

          {isVideoCreativeItem(editableItem) && projection.isSource && (
            <>
              {onOpenStoryboard && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SmartDisplayIcon />}
                  data-testid={AD_CREATIVE_BATCH.drawerOpenStoryboard}
                  onClick={() => onOpenStoryboard(editableItem.id)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Open storyboard ↗
                </Button>
              )}
              <VideoStudio
                projectId={projectId}
                item={editableItem}
                client={client}
                onRefreshItem={refreshCurrentItem}
                angle={angle}
                insightIndex={insightIndex}
              />
            </>
          )}

          <Accordion disableGutters data-testid={AD_CREATIVE_BATCH.drawerPromptAccordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Prompt & style</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Template: {editableItem.templateId || '—'} · Style: {editableItem.designCollectionId || 'default'}
                </Typography>
                {promptExperiment?.experiment.promptTemplate ? (
                  <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflowY: 'auto' }}>
                    {promptExperiment.experiment.promptTemplate}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">No prompt recorded.</Typography>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters data-testid={AD_CREATIVE_BATCH.drawerHistoryAccordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">{`Generation history (${experiments.length})`}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={0.75}>
                {experiments.length === 0 && (
                  <Typography variant="caption" color="text.secondary">No experiments yet.</Typography>
                )}
                {experiments.map(({ slotKey, experimentId, experiment }) => (
                  <Stack key={`${slotKey}-${experimentId}`} direction="row" spacing={1} alignItems="center">
                    <Chip size="small" variant="outlined" label={experiment.assetType === 'video' ? 'video' : 'image'} />
                    <Typography variant="caption" sx={{ flex: 1 }} noWrap>
                      {slotKey}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{experiment.status}</Typography>
                  </Stack>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      )}
      {editableItem && projection && previewImage && (
        <MediaLightbox
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          items={[{
            id: editableItem.id,
            kind: projection.previewMediaKind === 'video' ? 'video' : 'image',
            src: previewImage,
            title: localizedTitle,
            subtitle: `${editableItem.templateId ?? 'Creative'} · ${selectedLocale}`,
          }]}
          index={0}
          onIndexChange={() => undefined}
          renderMeta={() => <CreativeMetaSummary item={editableItem} angle={angle} />}
        />
      )}
    </Drawer>
  );
});

function isExactLocaleResourceMatch(
  resource: ProjectLocaleProfile | ProjectLocaleGlossary,
  projectId: string,
  sourceLocale: string,
  targetLocale: string,
): boolean {
  return resource.projectId === projectId
    && resource.status === 'active'
    && resource.sourceLocale === sourceLocale
    && resource.targetLocales.includes(targetLocale);
}

function localeProfileDisplayLabel(profile: ProjectLocaleProfile, index: number): string {
  const descriptors = [profile.context.targetMarket, profile.context.tone]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value));
  return [`Profile ${index + 1}`, ...descriptors.slice(0, 2)].join(' · ');
}

function localeProfileSummary(profile: ProjectLocaleProfile): string {
  const context = [
    profile.context.targetMarket ? `market ${profile.context.targetMarket}` : undefined,
    profile.context.tone ? `tone ${profile.context.tone}` : undefined,
    profile.context.marketDirection ? `direction ${profile.context.marketDirection}` : undefined,
  ].filter((value): value is string => Boolean(value));
  return context.length > 0
    ? `Saved profile supplies ${context.join(', ')}.`
    : `Saved profile supplies reusable context for ${profile.sourceLocale} → ${profile.targetLocales.join(', ')}.`;
}

function localeGlossaryDisplayLabel(glossary: ProjectLocaleGlossary, index: number): string {
  const terms = Object.keys(glossary.entries);
  const preview = terms.slice(0, 2).join(', ');
  return `Glossary ${index + 1} · ${preview || `${terms.length} approved terms`}`;
}

function localeGlossarySummary(glossary: ProjectLocaleGlossary): string {
  const termCount = Object.keys(glossary.entries).length;
  return `${termCount} approved ${termCount === 1 ? 'mapping' : 'mappings'} for ${glossary.sourceLocale} → ${glossary.targetLocales.join(', ')}.`;
}

function buildDrawerLocalizationPreflightRequest(options: {
  projectId: string;
  item: AdCreativeItem;
  targetLocale: string;
  component: AdCreativeLocalizationComponent;
  imageMode: AdCreativeImageLocalizationMode;
  imageSlotKeys: string[];
  imageEvidence: Record<string, AdCreativeSourceImageEvidenceView>;
  imageTargetVisibleText: Record<string, string>;
  videoSlotKey: string;
  videoVoiceEnabled: boolean;
  videoVoiceMode: 'replace_with_tts' | 'dub_when_supported';
  videoVoiceRightsSelection: AdCreativeVideoVoiceRightsSelection | null;
  approvedAudioArtifactId: string;
  videoOnScreenTextEnabled: boolean;
  videoTextInspectionSelection: AdCreativeVideoTextInspectionSelection | null;
  videoCulturalVisualEnabled: boolean;
  culturalVisualSelection: AdCreativeCulturalVisualSelection | null;
  localeProfileId?: string;
  glossaryRef?: string;
  textContext: {
    targetMarket: string;
    tone: string;
    marketDirection: string;
    glossary: string;
    claims: string;
    protectedTokens: string;
    userInstruction: string;
  };
}): AdCreativeLocalizationPreflightRequest | null {
  if (options.component === 'text') {
    const context = buildDrawerAiTextContext(options.textContext);
    return {
      projectId: options.projectId,
      itemId: options.item.id,
      component: 'text',
      mode: 'ai',
      targetLocales: [options.targetLocale],
      sourceLocale: options.item.defaultLanguage,
      ...(options.localeProfileId ? { localeProfileId: options.localeProfileId } : {}),
      ...(options.glossaryRef ? { glossaryRef: options.glossaryRef } : {}),
      ...(context ? { context } : {}),
    };
  }
  if (options.component === 'images') {
    if (options.imageSlotKeys.length === 0) return null;
    return {
      projectId: options.projectId,
      itemId: options.item.id,
      component: 'images',
      mode: options.imageMode,
      targetLocale: options.targetLocale,
      slotKeys: options.imageSlotKeys,
      model: DEFAULT_IMAGE_LOCALIZATION_MODEL,
      slotContexts: Object.fromEntries(options.imageSlotKeys.map((slotKey) => [slotKey, {
        inspection: { evidenceId: options.imageEvidence[slotKey]?.evidenceId ?? '' },
        targetVisibleText: splitExactLines(options.imageTargetVisibleText[slotKey] ?? ''),
      }])),
    };
  }
  if (!options.videoSlotKey) return null;
  const voiceOperation = options.videoVoiceMode === 'replace_with_tts' ? 'replace' : 'dub';
  if (options.videoVoiceRightsSelection
    && !options.videoVoiceRightsSelection.allowedOperations.includes(voiceOperation)) return null;
  if (options.videoVoiceEnabled && (
    options.videoVoiceMode !== 'replace_with_tts'
    || !options.videoVoiceRightsSelection
    || !options.approvedAudioArtifactId
  )) return null;
  if (options.videoOnScreenTextEnabled && (
    !options.videoTextInspectionSelection
    || options.videoTextInspectionSelection.evidence.creativeItemId !== options.item.id
    || options.videoTextInspectionSelection.evidence.slotKey !== options.videoSlotKey
    || options.videoTextInspectionSelection.evidence.source.historicalSourceRevision !== options.item.sourceRevision
    || options.videoTextInspectionSelection.replacements.length === 0
    || options.videoTextInspectionSelection.replacements.some((replacement) => !replacement.targetText.trim())
  )) return null;
  if (options.videoCulturalVisualEnabled && (
    !options.culturalVisualSelection
    || options.culturalVisualSelection.evidence.creativeItemId !== options.item.id
    || options.culturalVisualSelection.evidence.slotKey !== options.videoSlotKey
    || options.culturalVisualSelection.evidence.targetLocale !== options.targetLocale
    || options.culturalVisualSelection.evidence.source.historicalSourceRevision !== options.item.sourceRevision
    || options.culturalVisualSelection.evidence.scenes.length === 0
    || options.culturalVisualSelection.evidence.protectedReferences.length === 0
  )) return null;
  const tiers: AdCreativeVideoLocalizationPreflightRequest['tiers'] = ['captions'];
  if (options.videoVoiceEnabled) tiers.push('voice');
  if (options.videoOnScreenTextEnabled) tiers.push('on_screen_text');
  if (options.videoCulturalVisualEnabled) tiers.push('cultural_visuals');
  return {
    projectId: options.projectId,
    itemId: options.item.id,
    component: 'video',
    mode: 'ai',
    targetLocale: options.targetLocale,
    sourceLocale: options.item.defaultLanguage,
    videoSlotKey: options.videoSlotKey,
    tiers,
    models: {
      captions: DEFAULT_VIDEO_LOCALIZATION_MODEL,
      ...(options.videoVoiceEnabled ? { voice: DEFAULT_VIDEO_LOCALIZATION_MODEL } : {}),
      ...(options.videoOnScreenTextEnabled ? { on_screen_text: DEFAULT_VIDEO_LOCALIZATION_MODEL } : {}),
      ...(options.videoCulturalVisualEnabled
        ? { cultural_visuals: DEFAULT_CULTURAL_VIDEO_LOCALIZATION_MODEL }
        : {}),
    },
    ...(options.videoVoiceEnabled ? {
      voice: {
        mode: 'replace_with_tts' as const,
        approvedAudioArtifactId: options.approvedAudioArtifactId,
      },
    } : {}),
    ...(options.videoOnScreenTextEnabled && options.videoTextInspectionSelection ? {
      onScreenText: {
        inspectionEvidenceId: options.videoTextInspectionSelection.evidence.evidenceId,
        sourceSha256: options.videoTextInspectionSelection.evidence.source.sourceSha256,
        replacements: options.videoTextInspectionSelection.replacements.map((replacement) => ({
          regionId: replacement.regionId,
          targetText: replacement.targetText.trim(),
        })),
      },
    } : {}),
    ...(options.videoCulturalVisualEnabled && options.culturalVisualSelection ? {
      culturalVisuals: {
        evidenceId: options.culturalVisualSelection.evidence.evidenceId,
      },
    } : {}),
  };
}

function splitExactLines(value: string): string[] {
  return value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
}

function buildDrawerAiTextContext(input: {
  targetMarket: string;
  tone: string;
  marketDirection: string;
  glossary: string;
  claims: string;
  protectedTokens: string;
  userInstruction: string;
}): AdCreativeAiTextLocalizationContext | undefined {
  const context: AdCreativeAiTextLocalizationContext = {
    targetMarket: normalizedOptionalText(input.targetMarket),
    tone: normalizedOptionalText(input.tone),
    marketDirection: normalizedOptionalText(input.marketDirection),
    glossary: parseDrawerGlossary(input.glossary),
    claims: parseDrawerLineList(input.claims),
    protectedTokens: parseDrawerLineList(input.protectedTokens),
    userInstruction: normalizedOptionalText(input.userInstruction),
  };
  const definedContext = Object.fromEntries(
    Object.entries(context).filter(([, value]) => value !== undefined),
  ) as AdCreativeAiTextLocalizationContext;
  return Object.keys(definedContext).length > 0 ? definedContext : undefined;
}

function normalizedOptionalText(value: string): string | undefined {
  const normalized = value.trim();
  return normalized || undefined;
}

function parseDrawerLineList(value: string): string[] | undefined {
  const entries = value
    .split(/\r?\n/u)
    .map((entry) => entry.trim())
    .filter(Boolean);
  return entries.length > 0 ? entries : undefined;
}

function parseDrawerGlossary(value: string): Record<string, string> | undefined {
  const glossary: Record<string, string> = {};
  const lines = value.split(/\r?\n/u);
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trim();
    if (!line) continue;
    const separatorIndex = line.indexOf('=');
    const source = separatorIndex >= 0 ? line.slice(0, separatorIndex).trim() : '';
    const target = separatorIndex >= 0 ? line.slice(separatorIndex + 1).trim() : '';
    if (!source || !target) {
      throw new Error(`Glossary line ${index + 1} must use the format: source = target.`);
    }
    if (Object.prototype.hasOwnProperty.call(glossary, source)) {
      throw new Error(`Glossary line ${index + 1} repeats the source term "${source}".`);
    }
    glossary[source] = target;
  }
  return Object.keys(glossary).length > 0 ? glossary : undefined;
}

function buildDrawerLocalizationStartRequest(options: {
  projectId: string;
  itemId: string;
  preflight: AdCreativeLocalizationPreflight;
  localeProfileId?: string;
  glossaryRef?: string;
}): AdCreativeLocalizationStartRequest {
  const { preflight } = options;
  if (preflight.component === 'text') {
    return {
      projectId: options.projectId,
      itemId: options.itemId,
      component: 'text',
      mode: 'ai',
      targetLocales: preflight.targetLocales,
      sourceLocale: preflight.sourceLocale,
      model: preflight.model,
      scope: preflight.scope,
      context: preflight.context,
      ...(options.localeProfileId ? { localeProfileId: options.localeProfileId } : {}),
      ...(options.glossaryRef ? { glossaryRef: options.glossaryRef } : {}),
      preflightToken: preflight.preflightToken,
    };
  }
  if (preflight.component === 'images') {
    return {
      projectId: options.projectId,
      itemId: options.itemId,
      component: 'images',
      mode: preflight.request.mode,
      preflight,
    };
  }
  return {
    projectId: options.projectId,
    itemId: options.itemId,
    component: 'video',
    mode: 'ai',
    preflight,
  };
}

function localizationStartSnapshot(value: unknown): DrawerLocalizationJobSnapshot {
  return localizationJobSnapshot(value);
}

function CaptionVideoEvidenceReviewPanel(props: {
  review: AdCreativeCaptionReviewView | null;
  playbackSource: CaptionPlaybackSource | null;
  loading: boolean;
  error: string | null;
  canLoad: boolean;
  gate: AdCreativeVideoReviewGate;
  onLoad: () => void;
  onLoadedMetadata: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
  onPlaybackInvalid: () => void;
}): React.ReactElement {
  const {
    review,
    playbackSource,
    loading,
    error,
    canLoad,
    gate,
    onLoad,
    onLoadedMetadata,
    onPlaybackInvalid,
  } = props;
  const sidecarUrl = stableSegmentlyReviewAssetUrl(review?.artifact?.vttUrl);
  return (
    <Box
      data-testid={AD_CREATIVE_BATCH.drawerCaptionReviewPanel}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
    >
      <Stack spacing={1}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Evidence-bound video review
          </Typography>
          <Typography variant="caption" color="text.secondary">
            This saved review can be reopened in a later session. Accept stays locked until the exact bound derivative loads with its sealed dimensions and duration.
          </Typography>
        </Box>
        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" aria-live="polite">
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">Loading exact video evidence…</Typography>
          </Stack>
        )}
        {error && (
          <Alert severity="error" data-testid={AD_CREATIVE_BATCH.drawerCaptionReviewError}>
            {error}
          </Alert>
        )}
        {!loading && !review && (
          <Stack spacing={0.75}>
            <Alert severity="warning">
              {canLoad
                ? 'The exact saved review is not loaded. Video acceptance remains blocked.'
                : 'The current AI-generated video has no exact immutable component binding. Video acceptance remains blocked.'}
            </Alert>
            {canLoad && (
              <Button
                size="small"
                variant="outlined"
                data-testid={AD_CREATIVE_BATCH.drawerCaptionReviewLoad}
                onClick={onLoad}
              >
                Load exact video review
              </Button>
            )}
          </Stack>
        )}
        {review && (
          <>
            <Alert severity="info">
              This is a separately rendered Segmently derivative with immutable output evidence. The source video and every Meta/Facebook asset remain unchanged.
            </Alert>
            {playbackSource ? (
              <Box
                component="video"
                src={playbackSource.videoUrl}
                poster={playbackSource.posterUrl}
                controls
                preload="metadata"
                data-testid={AD_CREATIVE_BATCH.drawerVideoReviewPlayer}
                aria-label={`Localized video derivative · ${review.targetLocale}`}
                lang={review.targetLocale}
                dir={localeDirection(review.targetLocale)}
                onLoadStart={onPlaybackInvalid}
                onLoadedMetadata={onLoadedMetadata}
                onAbort={onPlaybackInvalid}
                onEmptied={onPlaybackInvalid}
                onError={onPlaybackInvalid}
                sx={{ width: '100%', maxHeight: 240, objectFit: 'contain', bgcolor: 'grey.100', borderRadius: 1 }}
              />
            ) : (
              <Alert severity="warning">
                Localized derivative playback is unavailable because its binding, output asset, optional caption or audio artifact, verifier evidence, or exact-output inspection does not match the current video component. Any immutable caption cues remain available below.
              </Alert>
            )}
            <Alert severity={gate.canAccept ? 'success' : 'warning'} aria-live="polite">
              {gate.reason}
            </Alert>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" label="Review required" color="warning" variant="outlined" />
              {playbackSource && (
                <Chip size="small" label="Verified derivative" color="success" variant="outlined" />
              )}
              <Typography variant="caption" color="text.secondary">
                {review.sourceLocale} → {review.targetLocale}
              </Typography>
              {sidecarUrl && (
                <Button
                  size="small"
                  component="a"
                  href={sidecarUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={AD_CREATIVE_BATCH.drawerCaptionReviewSidecarLink}
                >
                  Open WebVTT
                </Button>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {review.transcriptEvidence
                ? `Timing evidence: ${review.transcriptEvidence.verifierEvidenceId} · `
                : ''}
              Exact-output evidence: {review.verificationEvidence.evidenceId}
              {' · '}
              {playbackSource
                ? review.tier === 'on_screen_text'
                  ? `${review.verificationEvidence.checks.length}/${review.verificationEvidence.checks.length} verification checks passed · frame OCR, protected objects, and visual continuity verified`
                  : `${review.verificationEvidence.checks.length}/${review.verificationEvidence.checks.length} verification checks passed · language, burn-in, and pixel safe area verified`
                : 'evidence does not match the current creative state; playback blocked'}
            </Typography>
            {review.cues && review.cues.length > 0 && (
              <Box component="section" aria-label="Caption cue fallback">
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                  Caption cue fallback
                </Typography>
                <Stack spacing={0.75}>
                  {review.cues.map((cue, index) => (
                  <Box
                    key={cue.id}
                    data-testid={AD_CREATIVE_BATCH.drawerCaptionReviewCue(index)}
                    sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1 }}
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {formatCaptionCueTime(cue.startMs)} – {formatCaptionCueTime(cue.endMs)}
                        {cue.speaker ? ` · ${cue.speaker}` : ''}
                      </Typography>
                      <Typography
                        variant="body2"
                        lang={review.sourceLocale}
                        dir={localeDirection(review.sourceLocale)}
                        sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                      >
                        {cue.sourceText}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        lang={review.targetLocale}
                        dir={localeDirection(review.targetLocale)}
                        sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                      >
                        {cue.translatedText}
                      </Typography>
                    </Stack>
                  </Box>
                  ))}
                </Stack>
              </Box>
            )}
            {review.artifact && (
              <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                Caption artifact SHA-256: {review.artifact.vttSha256}
              </Typography>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
}

function localizationJobSnapshot(value: unknown): DrawerLocalizationJobSnapshot {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Localization response is missing progress data.');
  }
  const data = value as {
    component?: unknown;
    job?: unknown;
    task?: unknown;
  };
  if (data.component !== 'text' && data.component !== 'images' && data.component !== 'video') {
    throw new Error('Localization response has an invalid component.');
  }
  if (!data.job || typeof data.job !== 'object' || Array.isArray(data.job)) {
    throw new Error('Localization response is missing its job.');
  }
  const job = data.job as AdCreativeTextLocalizationJobView | AdCreativeMediaLocalizationJobView;
  if (!job.id || !job.status) throw new Error('Localization response has incomplete job identity.');
  const task = data.task && typeof data.task === 'object' && !Array.isArray(data.task)
    ? data.task as AdCreativeTextLocalizationTaskView | AdCreativeMediaLocalizationTaskView
    : undefined;
  return { component: data.component, job, task };
}

function localizationSnapshotStatus(snapshot: DrawerLocalizationJobSnapshot): string {
  const taskStatus = snapshot.task?.status;
  if (typeof taskStatus === 'string' && taskStatus) return taskStatus;
  return String(snapshot.job.status);
}

function isLocalizationSnapshotTerminal(snapshot: DrawerLocalizationJobSnapshot): boolean {
  const statuses = new Set([String(snapshot.job.status), String(snapshot.task?.status ?? '')]);
  return ['completed', 'partial', 'failed', 'error', 'cancelled']
    .some((status) => statuses.has(status));
}

function isLocalizationSnapshotSuccessful(snapshot: DrawerLocalizationJobSnapshot): boolean {
  const statuses = new Set([String(snapshot.job.status), String(snapshot.task?.status ?? '')]);
  return statuses.has('completed') || statuses.has('partial');
}

function hasCompletedCaptionTier(snapshot: DrawerLocalizationJobSnapshot): boolean {
  if (snapshot.component !== 'video' || localizationSnapshotStatus(snapshot) !== 'completed') return false;
  const jobResult = 'result' in snapshot.job ? snapshot.job.result : undefined;
  const taskResult = snapshot.task && 'result' in snapshot.task ? snapshot.task.result : undefined;
  const result = taskResult?.component === 'video'
    ? taskResult
    : jobResult?.component === 'video'
      ? jobResult
      : undefined;
  return result?.outcome === 'review_required' && result.appliedTiers.includes('captions');
}

interface CaptionPlaybackSource {
  videoUrl: string;
  posterUrl?: string;
}

function resolveCaptionPlaybackSource(input: {
  item: AdCreativeItem | null;
  review: AdCreativeCaptionReviewView | null;
  binding: AdCreativeVideoReviewSlotBindingView | undefined;
  componentBinding: AdCreativeVideoReviewComponentBindingView | undefined;
  currentSourceComponentHash: string | undefined;
  currentComponentStatus: string | undefined;
  selectedLocale: string;
  videoSlotKey: string;
}): CaptionPlaybackSource | null {
  const {
    item,
    review,
    binding,
    componentBinding,
    currentSourceComponentHash,
    currentComponentStatus,
    selectedLocale,
    videoSlotKey,
  } = input;
  if (!item
    || item.entityKind !== 'segmently_copy'
    || !review
    || !binding
    || !componentBinding
    || componentBinding.bindingVersion !== 1
    || componentBinding.targetLocale !== selectedLocale
    || !isCanonicalSha256(currentSourceComponentHash)
    || componentBinding.sourceComponentHash !== currentSourceComponentHash
    || binding !== componentBinding.slots[videoSlotKey]
    || currentComponentStatus === 'stale'
    || currentComponentStatus === 'missing'
    || currentComponentStatus === 'archived') return null;

  const generatedVideoSlotKeys = Object.entries(item.localizedMedia ?? {})
    .filter(([, slot]) => slot.mediaKind === 'video')
    .filter(([, slot]) => getExactMediaEntry(slot.translations ?? {}, selectedLocale)?.provenance === 'generated')
    .map(([slotKey]) => slotKey)
    .sort();
  const boundSlotKeys = Object.keys(componentBinding.slots).sort();
  // The current drawer verifies one loaded media element. Until it tracks a
  // separate loaded-metadata proof per slot, multi-video acceptance must stay
  // closed even when every server binding is present.
  if (generatedVideoSlotKeys.length !== 1
    || generatedVideoSlotKeys.length !== boundSlotKeys.length
    || generatedVideoSlotKeys.some((slotKey, index) => slotKey !== boundSlotKeys[index])
    || boundSlotKeys.some((slotKey) => !isCurrentVideoSlotBinding({
      item,
      sourceLocale: item.defaultLanguage,
      targetLocale: selectedLocale,
      currentSourceComponentHash,
      slotKey,
      binding: componentBinding.slots[slotKey],
    }))) {
    return null;
  }

  if (!review.renderedDerivative
    || !review.verificationEvidence
    || !review.verificationEvidence.lineage
    || !review.verificationEvidence.output
    || !review.verificationEvidence.exactOutputInspection
    || !Array.isArray(review.verificationEvidence.checks)
    || !Array.isArray(review.verificationEvidence.tiers)
    || !Array.isArray(review.verificationEvidence.exactOutputInspection.sampledTimestampsMs)
    || !Array.isArray(review.verification?.checks)) {
    return null;
  }
  const evidence = review.verificationEvidence;
  const inspection = evidence.exactOutputInspection;
  const derivative = review.renderedDerivative;
  const hasCaptions = evidence.tiers.includes('captions');
  const hasVoice = evidence.tiers.includes('voice');
  const captionInspection = 'checkedCueIds' in inspection ? inspection : undefined;
  const onScreenInspection = 'checkedRegionIds' in inspection ? inspection : undefined;
  if (review.component !== 'video'
    || review.tier !== binding.tier
    || review.jobId !== binding.jobId
    || review.creativeItemId !== item.id
    || review.sourceLocale !== item.defaultLanguage
    || review.targetLocale !== selectedLocale
    || review.slotKey !== videoSlotKey
    || review.sourceRevision !== binding.sourceRevision
    || review.inputManifestHash !== binding.inputManifestHash
    || review.reviewStatus !== 'review_required'
    || review.sidecarAvailable !== hasCaptions
    || !binding.taskId.trim()
    || binding.sourceComponentHash !== currentSourceComponentHash
    || !isCanonicalSha256(binding.inputManifestHash)
    || !isCanonicalSha256(binding.sourceSha256)
    || !isCanonicalSha256(binding.outputSha256)
    || !isCanonicalSha256(binding.verificationEvidenceHash)
    || !isCanonicalSha256(binding.outputInspectionHash)
    || review.verification.passed !== true
    || review.verification.checks.length === 0
    || review.verification.checks.some((check) => !check.passed)
    || evidence.evidenceId !== binding.verificationEvidenceId
    || evidence.evidenceHash !== binding.verificationEvidenceHash
    || !isCanonicalSha256(evidence.evidenceHash)
    || evidence.immutable !== true
    || !evidence.processVersion.trim()
    || !evidence.createdAt.trim()
    || evidence.targetLocale !== selectedLocale
    || evidence.lineage.creativeItemId !== item.id
    || evidence.lineage.slotKey !== videoSlotKey
    || evidence.lineage.sourceRevision !== binding.sourceRevision
    || evidence.lineage.sourceAssetId !== binding.sourceAssetId
    || evidence.lineage.sourceSha256 !== binding.sourceSha256
    || evidence.lineage.inputManifestHash !== binding.inputManifestHash
    || evidence.captionArtifactId !== binding.captionArtifactId
    || evidence.tiers.length === 0
    || evidence.tiers[evidence.tiers.length - 1] !== binding.tier
    || evidence.checks.length === 0
    || evidence.checks.some((check) => check.passed !== true)
    || inspection.immutable !== true
    || inspection.evidenceId !== binding.outputInspectionEvidenceId
    || inspection.contentHash !== binding.outputInspectionHash
    || !isCanonicalSha256(inspection.contentHash)
    || !inspection.provider.trim()
    || !inspection.model.trim()
    || !inspection.version.trim()
    || inspection.projectId !== item.projectId
    || inspection.creativeItemId !== item.id
    || inspection.slotKey !== videoSlotKey
    || inspection.sourceRevision !== binding.sourceRevision
    || inspection.outputSha256 !== binding.outputSha256
    || inspection.targetLocale !== selectedLocale) {
    return null;
  }

  if (hasCaptions) {
    const artifact = review.artifact;
    const transcript = review.transcriptEvidence;
    const cues = review.cues;
    if (!artifact
      || !transcript
      || !cues?.length
      || !binding.captionArtifactId
      || !isCanonicalSha256(binding.captionArtifactHash)
      || artifact.immutable !== true
      || artifact.id !== binding.captionArtifactId
      || artifact.contentHash !== binding.captionArtifactHash
      || !isCanonicalSha256(artifact.contentHash)
      || !isCanonicalSha256(artifact.vttSha256)
      || !stableSegmentlyReviewAssetUrl(artifact.vttUrl)
      || !artifact.provider.trim()
      || !artifact.model.trim()
      || !artifact.createdAt.trim()
      || artifact.promptVersionRefs.length === 0
      || artifact.promptVersionRefs.some((reference) => !reference.trim())
      || cues.some((cue, index) => (
        !Number.isSafeInteger(cue.startMs)
        || !Number.isSafeInteger(cue.endMs)
        || cue.startMs < 0
        || cue.endMs <= cue.startMs
        || cue.endMs > derivative.durationMs
        || !cue.translatedText.trim()
        || (index > 0 && cue.startMs < cues[index - 1].startMs)
      ))) return null;
  } else if (review.artifact
    || review.transcriptEvidence
    || review.cues
    || binding.captionArtifactId
    || binding.captionArtifactHash) {
    return null;
  }

  if (review.tier === 'on_screen_text') {
    const textEvidence = review.onScreenTextEvidence;
    if (!onScreenInspection
      || !textEvidence
      || binding.videoTextInspectionEvidenceId !== textEvidence.evidenceId
      || binding.videoTextInspectionEvidenceHash !== textEvidence.evidenceContentHash
      || textEvidence.sourceComponentHash !== binding.sourceComponentHash
      || onScreenInspection.inspectionEvidenceId !== textEvidence.evidenceId
      || onScreenInspection.inspectionEvidenceContentHash !== textEvidence.evidenceContentHash
      || onScreenInspection.sourceSha256 !== binding.sourceSha256
      || !isCanonicalSha256(onScreenInspection.comparisonSha256)
      || !isCanonicalSha256(onScreenInspection.comparisonCompositionHash)
      || !isCanonicalSha256(onScreenInspection.renderRecipeHash)
      || !isCanonicalSha256(onScreenInspection.sourceFrameSampleSetHash)
      || !isCanonicalSha256(onScreenInspection.outputFrameSampleSetHash)
      || onScreenInspection.frameOcr.passed !== true
      || onScreenInspection.protectedObjects.passed !== true
      || onScreenInspection.visualContinuity.passed !== true
      || onScreenInspection.checkedRegionIds.join(',') !== textEvidence.checkedRegionIds.join(',')
      || onScreenInspection.sampledTimestampsMs.join(',') !== textEvidence.sampledTimestampsMs.join(',')) {
      return null;
    }
  } else if (!captionInspection
    || review.onScreenTextEvidence
    || binding.videoTextInspectionEvidenceId
    || binding.videoTextInspectionEvidenceHash
    || captionInspection.observedLocale !== selectedLocale
    || !isCanonicalSha256(captionInspection.frameSampleSetHash)
    || captionInspection.targetLanguage.passed !== true
    || captionInspection.pixelSafeArea.passed !== true
    || captionInspection.burnInRender.passed !== true
    || captionInspection.checkedCueIds.length !== review.cues?.length
    || captionInspection.checkedCueIds.some((cueId, index) => cueId !== review.cues?.[index]?.id)) {
    return null;
  }

  const slot = item.localizedMedia?.[videoSlotKey];
  if (!slot || slot.mediaKind !== 'video') return null;
  const sourceEntry = getExactMediaEntry(slot.translations ?? {}, review.sourceLocale);
  const sourceAsset = sourceEntry?.disabled ? undefined : sourceEntry?.asset;
  const targetEntry = getExactMediaEntry(slot.translations ?? {}, selectedLocale);
  const targetAsset = targetEntry?.disabled ? undefined : targetEntry?.asset;
  const sourceSha256 = sourceAsset?.sha256?.trim().toLowerCase();
  const reviewedSha256 = review.sourceAsset.sha256.trim().toLowerCase();
  const derivativeSha256 = derivative.sha256.trim().toLowerCase();
  const evidenceOutput = evidence.output;
  const videoUrl = stableSegmentlyReviewAssetUrl(derivative.playbackUrl);
  const targetVideoUrl = stableSegmentlyReviewAssetUrl(targetAsset?.original);
  const posterUrl = derivative.posterUrl
    ? stableSegmentlyReviewAssetUrl(derivative.posterUrl)
    : undefined;
  if (!sourceAsset
    || !targetAsset
    || targetEntry?.provenance !== 'generated'
    || !videoUrl
    || videoUrl !== targetVideoUrl
    || sourceAsset.assetId !== review.sourceAsset.assetId
    || sourceAsset.assetId !== binding.sourceAssetId
    || !sourceSha256
    || sourceSha256 !== reviewedSha256
    || sourceSha256 !== binding.sourceSha256
    || !sourceAsset.mimeType?.startsWith('video/')
    || sourceAsset.mimeType !== review.sourceAsset.mimeType
    || sourceAsset.durationMs !== review.sourceAsset.durationMs
    || (hasCaptions && (
      sourceSha256 !== review.transcriptEvidence?.sourceSha256.trim().toLowerCase()
      || sourceAsset.transcriptAssetId !== review.transcriptEvidence?.artifactId
      || !review.transcriptEvidence?.verifierEvidenceId.trim()
    ))
    || !review.sourceAsset.mimeType.startsWith('video/')
    || targetAsset.assetId !== binding.outputAssetId
    || targetAsset.sha256 !== binding.outputSha256
    || targetAsset.mimeType !== binding.outputMimeType
    || targetAsset.width !== binding.outputWidth
    || targetAsset.height !== binding.outputHeight
    || targetAsset.durationMs !== binding.outputDurationMs
    || targetAsset.captionsAssetId !== binding.captionArtifactId
    || targetAsset.audioTrackAssetId !== binding.audioAssetId
    || hasVoice !== Boolean(binding.audioAssetId)
    || !derivative.assetId.trim()
    || !/^[a-f0-9]{64}$/.test(derivativeSha256)
    || derivative.assetId !== binding.outputAssetId
    || derivativeSha256 !== binding.outputSha256
    || derivative.mimeType !== binding.outputMimeType
    || derivative.width !== binding.outputWidth
    || derivative.height !== binding.outputHeight
    || derivative.durationMs !== binding.outputDurationMs
    || derivative.mimeType !== evidenceOutput.mimeType
    || !derivative.mimeType.startsWith('video/')
    || derivativeSha256 !== evidenceOutput.sha256.toLowerCase()
    || derivative.width !== evidenceOutput.width
    || derivative.height !== evidenceOutput.height
    || derivative.durationMs !== evidenceOutput.durationMs
    || evidenceOutput.sha256 !== binding.outputSha256
    || evidenceOutput.mimeType !== binding.outputMimeType
    || evidenceOutput.width !== binding.outputWidth
    || evidenceOutput.height !== binding.outputHeight
    || evidenceOutput.durationMs !== binding.outputDurationMs
    || !Number.isSafeInteger(evidenceOutput.byteSize)
    || evidenceOutput.byteSize <= 0
    || !Number.isSafeInteger(derivative.width)
    || derivative.width <= 0
    || !Number.isSafeInteger(derivative.height)
    || derivative.height <= 0
    || !Number.isSafeInteger(derivative.durationMs)
    || derivative.durationMs <= 0
    || Boolean(derivative.posterUrl) !== Boolean(targetAsset.small)
    || Boolean(derivative.posterAssetId) !== Boolean(targetAsset.posterAssetId)
    || derivative.posterAssetId !== targetAsset.posterAssetId
    || (derivative.posterUrl !== undefined && !posterUrl)
    || (posterUrl !== undefined && posterUrl !== stableSegmentlyReviewAssetUrl(targetAsset.small))
    || inspection.sampledTimestampsMs.length === 0
    || inspection.sampledTimestampsMs.some((timestamp) => (
      !Number.isSafeInteger(timestamp) || timestamp < 0 || timestamp > derivative.durationMs
    ))) {
    return null;
  }

  return {
    videoUrl,
    ...(posterUrl ? { posterUrl } : {}),
  };
}

function isCurrentVideoSlotBinding(input: {
  item: AdCreativeItem;
  sourceLocale: string;
  targetLocale: string;
  currentSourceComponentHash: string;
  slotKey: string;
  binding: AdCreativeVideoReviewSlotBindingView | undefined;
}): boolean {
  const { item, sourceLocale, targetLocale, currentSourceComponentHash, slotKey, binding } = input;
  const slot = item.localizedMedia?.[slotKey];
  const source = slot ? getExactMediaEntry(slot.translations ?? {}, sourceLocale) : undefined;
  const target = slot ? getExactMediaEntry(slot.translations ?? {}, targetLocale) : undefined;
  const sourceAsset = source?.disabled ? undefined : source?.asset;
  const targetAsset = target?.disabled ? undefined : target?.asset;
  return Boolean(slot?.mediaKind === 'video'
    && binding
    && binding.jobId.trim()
    && binding.taskId.trim()
    && binding.sourceRevision.trim()
    && binding.sourceComponentHash === currentSourceComponentHash
    && isCanonicalSha256(binding.inputManifestHash)
    && isCanonicalSha256(binding.sourceComponentHash)
    && isCanonicalSha256(binding.sourceSha256)
    && isCanonicalSha256(binding.outputSha256)
    && Boolean(binding.captionArtifactId) === Boolean(binding.captionArtifactHash)
    && (!binding.captionArtifactHash || isCanonicalSha256(binding.captionArtifactHash))
    && isCanonicalSha256(binding.verificationEvidenceHash)
    && isCanonicalSha256(binding.outputInspectionHash)
    && sourceAsset
    && targetAsset
    && target?.provenance === 'generated'
    && sourceAsset.assetId === binding.sourceAssetId
    && sourceAsset.sha256 === binding.sourceSha256
    && targetAsset.assetId === binding.outputAssetId
    && targetAsset.sha256 === binding.outputSha256
    && targetAsset.mimeType === binding.outputMimeType
    && targetAsset.width === binding.outputWidth
    && targetAsset.height === binding.outputHeight
    && targetAsset.durationMs === binding.outputDurationMs
    && targetAsset.captionsAssetId === binding.captionArtifactId
    && targetAsset.audioTrackAssetId === binding.audioAssetId);
}

function isCanonicalSha256(value: string | undefined): value is string {
  return Boolean(value && /^[a-f0-9]{64}$/.test(value));
}

function stableSegmentlyReviewAssetUrl(value: string | undefined): string | null {
  const normalized = value?.trim();
  if (!normalized) return null;
  try {
    const url = new URL(normalized);
    if (url.protocol !== 'https:'
      || url.username
      || url.password
      || url.search
      || url.hash
      || (url.hostname !== 'segmently.ai' && !url.hostname.endsWith('.segmently.ai'))
      || !url.pathname.startsWith('/assets/')) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

function hasExactSourceVideoAsset(item: AdCreativeItem, slotKey: string): boolean {
  if (item.entityKind !== 'segmently_copy') return false;
  const slot = item.localizedMedia?.[slotKey];
  if (!slot || slot.mediaKind !== 'video') return false;
  const translations = slot.translations ?? {};
  const sourceEntry = Object.prototype.hasOwnProperty.call(translations, item.defaultLanguage)
    ? translations[item.defaultLanguage]
    : undefined;
  const asset = sourceEntry?.disabled ? undefined : sourceEntry?.asset;
  return Boolean(asset
    && asset.assetId?.trim()
    && asset.original?.trim()
    && asset.mimeType?.startsWith('video/')
    && /^[a-f0-9]{64}$/.test(asset.sha256 ?? '')
    && Number.isSafeInteger(asset.width)
    && Number.isSafeInteger(asset.height)
    && Number.isSafeInteger(asset.durationMs)
    && (asset.width ?? 0) > 0
    && (asset.height ?? 0) > 0
    && (asset.durationMs ?? 0) > 0);
}

/** Build a temporary, plain-text WebVTT sidecar from server-validated review cues. */
export function buildCaptionReviewWebVtt(
  cues: AdCreativeCaptionReviewView['cues'],
): string | null {
  if (!cues?.length || cues.some((cue, index) => (
    !Number.isSafeInteger(cue.startMs)
    || !Number.isSafeInteger(cue.endMs)
    || cue.startMs < 0
    || cue.endMs <= cue.startMs
    || !cue.translatedText.trim()
    || (index > 0 && cue.startMs < cues[index - 1].startMs)
  ))) {
    return null;
  }

  const cueBlocks = cues.map((cue) => [
    `${formatWebVttTimestamp(cue.startMs)} --> ${formatWebVttTimestamp(cue.endMs)}`,
    escapeWebVttPlainText(cue.translatedText),
  ].join('\n'));
  return `WEBVTT\n\n${cueBlocks.join('\n\n')}\n`;
}

function formatWebVttTimestamp(milliseconds: number): string {
  const safe = Math.max(0, Math.floor(milliseconds));
  const hours = Math.floor(safe / 3_600_000);
  const minutes = Math.floor((safe % 3_600_000) / 60_000);
  const seconds = Math.floor((safe % 60_000) / 1_000);
  const millis = safe % 1_000;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

function escapeWebVttPlainText(value: string): string {
  return value
    .replace(/\u0000/g, '\uFFFD')
    .replace(/\r\n?/g, '\n')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function formatCaptionCueTime(milliseconds: number): string {
  const safe = Math.max(0, Math.floor(milliseconds));
  const minutes = Math.floor(safe / 60_000);
  const seconds = Math.floor((safe % 60_000) / 1_000);
  const millis = safe % 1_000;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

function isLocalizationSnapshotResumable(snapshot: DrawerLocalizationJobSnapshot): boolean {
  const statuses = new Set([String(snapshot.job.status), String(snapshot.task?.status ?? '')]);
  if (['partial', 'failed', 'error', 'cancelled'].some((status) => statuses.has(status))) return true;
  const jobOutcome = 'result' in snapshot.job ? snapshot.job.result?.outcome : undefined;
  const taskOutcome = snapshot.task && 'result' in snapshot.task
    ? snapshot.task.result?.outcome
    : undefined;
  return jobOutcome === 'partial' || taskOutcome === 'partial';
}

function isLocalizationPreflightSupported(preflight: AdCreativeLocalizationPreflight): boolean {
  return preflight.component === 'text' || preflight.supported;
}

function collectLocalizationUnsupportedReasons(
  preflight: AdCreativeLocalizationPreflight,
): Array<{ code: string; message: string }> {
  if (preflight.component === 'text') return [];
  const reasons = preflight.component === 'images'
    ? preflight.slots.flatMap((slot) => slot.unsupportedReasons)
    : [
      ...(preflight.transcriptCapability.unsupportedReasons ?? []),
      ...preflight.tiers.flatMap((tier) => tier.unsupportedReasons),
    ];
  return Array.from(new Map(reasons.map((reason) => [
    `${reason.code}\u0000${reason.message}`,
    reason,
  ])).values());
}

function localizationPreflightEstimate(preflight: AdCreativeLocalizationPreflight): string {
  if (preflight.component === 'text') {
    return `${formatUsd(preflight.costEstimate.estimatedUsd)} estimated · ${preflight.costEstimate.totalTokens.toLocaleString()} tokens`;
  }
  if (preflight.component === 'images') {
    return `${formatUsd(preflight.estimatedCostUsd)} estimated · ${preflight.slots.length} slot${preflight.slots.length === 1 ? '' : 's'}`;
  }
  const cost = preflight.costEstimate;
  if (!cost?.complete) {
    return `Cost unavailable · ${preflight.tiers.length} tier${preflight.tiers.length === 1 ? '' : 's'}`;
  }
  const tokens = cost.tiers.reduce((total, tier) => (
    total + tier.estimatedInputTokens + tier.estimatedOutputTokens
  ), 0);
  const generatedSeconds = cost.tiers.reduce((total, tier) => (
    total + (tier.estimatedGeneratedSeconds ?? 0)
  ), 0);
  const workload = [
    generatedSeconds > 0 ? `${generatedSeconds.toLocaleString()} generated seconds` : '',
    tokens > 0 ? `${tokens.toLocaleString()} tokens` : '',
  ].filter(Boolean).join(' + ');
  return `${formatMinorCurrency(cost.estimatedAmountMinor, cost.currency)} estimated${workload ? ` · ${workload}` : ''}`;
}

function localizationCapabilitySummary(preflight: AdCreativeLocalizationPreflight): string {
  if (preflight.component === 'text') {
    return `${preflight.modelCapability.displayName} · ${preflight.provider}`;
  }
  if (preflight.component === 'images') {
    const providers = Array.from(new Set(preflight.slots.map((slot) => slot.capability.adapter.provider)));
    return `${preflight.request.mode.replace(/_/g, ' ')} · ${providers.join(', ') || 'no provider'}`;
  }
  const providers = Array.from(new Set(preflight.tiers.map((tier) => tier.adapter.provider)));
  return `${preflight.request.tiers.join(', ').replace(/_/g, ' ')} · ${providers.join(', ') || 'no provider'}`;
}

function localizationStatusCopy(snapshot: DrawerLocalizationJobSnapshot): string {
  const component = snapshot.component === 'images' ? 'Image' : snapshot.component[0].toUpperCase() + snapshot.component.slice(1);
  const status = localizationSnapshotStatus(snapshot).replace(/_/g, ' ');
  const phase = snapshot.task && 'currentPhaseKey' in snapshot.task && snapshot.task.currentPhaseKey
    ? ` · ${String(snapshot.task.currentPhaseKey).replace(/_/g, ' ')}`
    : '';
  return `${component} localization · ${status}${phase}`;
}

function localizationJobError(snapshot: DrawerLocalizationJobSnapshot): string | null {
  const taskError = snapshot.task?.error;
  if (typeof taskError === 'string' && taskError) return taskError;
  if ('error' in snapshot.job && typeof snapshot.job.error === 'string' && snapshot.job.error) {
    return snapshot.job.error;
  }
  if (snapshot.component === 'text' && 'localeResults' in snapshot.job) {
    const failed = Object.values(snapshot.job.localeResults)
      .find((result) => result.status === 'failed' && result.errorMessage);
    if (failed?.errorMessage) return failed.errorMessage;
  }
  return null;
}

function localizationResponseError(
  response: { error?: string; code?: string },
  fallback: string,
): string {
  if (response.code === 'missing_project_locale_config') {
    return 'Project locale settings are required before AI localization. Configure project languages, then run preflight again.';
  }
  return response.error || fallback;
}

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: value > 0 && value < 0.01 ? 4 : 2,
    maximumFractionDigits: 4,
  }).format(value);
}

function formatMinorCurrency(value: number, currency: string): string {
  const amount = Number.isSafeInteger(value) && value >= 0 ? value / 100 : 0;
  const normalizedCurrency = /^[A-Z]{3}$/.test(currency) ? currency : 'USD';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: normalizedCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatMediaBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return 'unknown size';
  if (value < 1024 * 1024) return `${Math.ceil(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function formatMediaDuration(valueMs: number): string {
  if (!Number.isSafeInteger(valueMs) || valueMs <= 0) return 'unknown duration';
  const minutes = Math.floor(valueMs / 60_000);
  const seconds = Math.floor((valueMs % 60_000) / 1_000);
  const milliseconds = valueMs % 1_000;
  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
}

function localeStatusColor(
  status: string,
): 'success' | 'warning' | 'error' | 'info' | 'default' {
  if (status === 'source' || status === 'ready') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'stale' || status === 'partial') return 'warning';
  if (status === 'generating' || status === 'review_required') return 'info';
  return 'default';
}

function componentLabel(component: 'text' | 'images' | 'video' | 'destination'): string {
  if (component === 'text') return 'Text';
  if (component === 'images') return 'Images';
  if (component === 'video') return 'Video';
  return 'Destination';
}

function collectExperiments(item: AdCreativeItem | null): Array<{
  slotKey: string;
  experimentId: string;
  experiment: AiAssetExperiment;
}> {
  if (!item) return [];
  const aiAssets = item.aiAssets as Record<string, { experiments?: Record<string, AiAssetExperiment> }> | undefined;
  if (!aiAssets) return [];
  return Object.entries(aiAssets).flatMap(([slotKey, slot]) =>
    Object.entries(slot.experiments ?? {}).map(([experimentId, experiment]) => ({ slotKey, experimentId, experiment })),
  );
}

function copyConstraintsFromManifest(
  manifest: AdCreativeLocalizationManifest,
): Partial<Record<CreativeLocalizedCopyField, CreativeCopyConstraint>> {
  const result: Partial<Record<CreativeLocalizedCopyField, CreativeCopyConstraint>> = {};
  for (const { key } of COPY_FIELDS) {
    const entry = manifest.entries.find((candidate) => candidate.path === `localizedCopy.${key}`);
    const maxChars = Number((entry?.constraints as Record<string, unknown> | undefined)?.maxChars);
    result[key] = {
      ...(Number.isSafeInteger(maxChars) && maxChars > 0 ? { maxChars } : {}),
      countingUnit: 'grapheme_cluster',
    };
  }
  return result;
}

function getExactDestination(
  item: AdCreativeItem,
  locale: string,
): AdCreativeDestinationRef | undefined {
  return Object.prototype.hasOwnProperty.call(item.localizedDestinations ?? {}, locale)
    ? item.localizedDestinations[locale]
    : undefined;
}

function destinationModeFromRef(destination?: AdCreativeDestinationRef): DrawerDestinationMode {
  if (destination?.type === 'web_placement') return 'funnel';
  if (destination?.type === 'article') return 'article';
  if (destination?.type === 'none') return 'none';
  return 'url';
}

function destinationValueFromDraft(input: {
  mode: DrawerDestinationMode;
  url: string;
  option?: AdCreativeDestinationOptionView;
}): AdCreativeDestinationRef {
  if (input.mode === 'none') return { type: 'none' };
  if (input.mode === 'url') {
    if (!isHttpUrl(input.url)) throw new Error('Destination URL must use http:// or https://.');
    return { type: 'url', url: input.url.trim() };
  }
  if (!input.option || input.option.kind !== input.mode) {
    throw new Error(`Choose a published project ${input.mode} before saving.`);
  }
  return { ...input.option.destination };
}

function getExactMediaEntry(
  translations: Record<string, AdCreativeLocalizedMediaEntry>,
  locale: string,
): AdCreativeLocalizedMediaEntry | undefined {
  return Object.prototype.hasOwnProperty.call(translations, locale)
    ? translations[locale]
    : undefined;
}

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}
